import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import track from "../../../../utils/track";
import generalService from "../../../../service/general-service";
import { startTeaser } from "../jingyue-teaser-cache";
import PaymentService from "../../../../service/payment-service";
import InvoiceSheet from "../../../dialog/invoice-sheet/invoice-sheet";
import {
  openEcpayWindow,
  submitToEcpay,
} from "../../../../utils/ecpay-redirect";
import JingyueStoryIntro from "./jingyue-story-intro";
import PurchaseSheet from "../../../dialog/purchase-sheet/purchase-sheet";
import "./jingyue-story-style.scss";

//—— 鏡月・紫微斗數：解命後的故事頁 ——
//
//  流程上接在 ziwei-jingyue-page 的最後一題（email）之後：
//  解命影片播完 → 導到這裡。
//
//  和前面那一頁的差別是「版型」而不只是內容：
//  劇情頁是滿版固定高度、一次只給一幕、不能捲；這一頁是一路捲到底的長頁。
//  兩種版型塞進同一個元件會變成一堆 if，所以拆成兩支。
//
//  內容全在下面 STORY 這張表裡，元件只負責排版 ——
//  改文案、加段落、調順序都只動那張表，作法同劇情頁的 SCENES。

//回上一頁（劇情頁）的去處。直接貼網址進來時沒有上一頁，要有個退路。
const BACK_TO = "/product/ziwei-jingyue";

//—— 開發用的替身問診資料 ——
//  location state 不進網址，也撐不過重新整理 —— 直接開這一頁或按 F5，
//  intake 就是空的，頁面會把人導回劇情頁。
//
//  但這一頁的版型要反覆調（圖、對話框位置、字級都得看著畫面改），
//  每改一次就從問診走十題不切實際。開發時用一組固定的假資料頂著，
//  網址列直接打 /product/ziwei-jingyue/story 就能看到完整畫面。
//
//  只在 npm start 生效：NODE_ENV 在建置時就會被換成字面值 "production"，
//  所以正式 build 裡這個常數是 null，整段假資料也會被搖掉，不會進 bundle。
//
//  生日刻意挑一個「有時辰、有出生地」的：這樣真太陽時校正那條路徑
//  平常就會被走到，不會等到上線才第一次跑。
//底部按鈕的觸發點：使用者滑到這個元素，按鈕就出現，之後不再消失。
//  放在「是為了那件事來的吧？」那一行 —— 在那之前他還在鋪陳，
//  按鈕太早出現會變成在打斷故事。
const CTA_TRIGGER_ID = "jingyue-story-cta-trigger";

//付款開在新分頁之後，這一頁靠輪詢知道結果（見 watchOrder）。
//  3 秒一次：使用者在綠界刷完卡大概會停留幾秒看成功畫面，
//  這個間隔讓他切回來時多半已經變了，又不至於打太兇。
//  15 分鐘封頂：綠界那張單本來就會過期，再等下去沒有意義。
const PAY_POLL_MS = 3000;
const PAY_POLL_LIMIT_MS = 15 * 60 * 1000;

//—— 購買方案 ——
//
const SAMPLE_INTAKE = {
  relation: "self",
  name: "王小明",
  gender: "male",
  birthDate: "1995/12/08",
  birthTime: "16:20",
  birthPlace: { label: "臺灣 臺北市", lat: 25.033, lng: 121.5654 },
  orientation: "hetero",
  relationship: "single",
  career: "employed",
  concern: "",
  email: "sample@example.com",
};

//這一頁在什麼情況下可以用範例資料顯示：
//
//  開發時（npm start）—— 版型要反覆調，每次從問診走十題不實際。
//  網址帶 ?preview  —— 給人看用的。location.state 不進網址，
//                      所以把連結傳給別人時對方一定沒有問診資料。
//
//  預覽模式刻意做成「網址帶參數」而不是另開一條路由：
//  它走的是同一支元件、同一組 block，看到的就是真的那一頁，
//  不會出現「預覽版跟正式版長得不一樣」這種事。
//  刻意不取 use 開頭的名字：那會被 ESLint 的 react-hooks 規則
//  當成 Hook，而它是在三元運算式裡呼叫的，會被判定成「條件式呼叫 Hook」。
const wantsSampleIntake = (search) =>
  process.env.NODE_ENV === "development" ||
  new URLSearchParams(search).has("preview");

//—— 問診資料 ——
//
//  劇情頁走完會把答案放在 location state 的 intake 裡帶過來（見 ziwei-jingyue-page
//  最後一幕的導頁）。欄位名就是 SCENES 裡的 field，值也沒有轉換過：
//
//    relation      看誰的八字   self / family / partner / friend / other
//    birthDate     出生年月日   "1990/01/01"（國曆）
//    birthTime     出生時間     "12:30"，或 "unknown"
//    birthPlace    出生地       BirthplaceField 給的物件（含經緯度）
//    gender        性別         "male" / "female"（排盤要的 "1"/"0" 之後再轉）
//    name          稱呼
//    orientation   取向         hetero / lgbt
//    relationship  感情狀態     single / crush / dating / breakup / married
//    career        工作狀態     student / seeking / employed / business / freelance / none
//    concern       想問的煩惱   可留白
//    email         通知信箱
//
//  重新整理就會沒有 —— location state 不進網址也不落地。
//  所以沒接到資料時不硬撐著畫面，直接把人送回劇情頁從頭走一次。

//—— 故事內容 ——
//
//  整頁是一條長捲軸，由一個個 block 串起來，中間沒有間隙也沒有頁的概念。
//  要加內容就往這張表後面加一筆，版型不用動。
//
//  目前有四種 block：
//    image  滿版圖片，高度依原圖比例，接縫處對齊沒有空隙；
//           可疊任意顆對話框（bubbles）或字幕（captions）
//    text   黑底文字，一行一句（lines 給幾行就幾行）；
//           帶 size: "lg" 的話整段字一起放大，用在要壓過周圍的段落；
//           帶 head / tail 的話上緣／下緣多留一截，用來跟前後拉開距離
//    panel  花紋背景上嵌一塊黑板子，寫標題 + 大段內文（可接動態資料）
//    chart  四柱命盤表（十神／天干／地支／藏干），資料全部來自後端
//    chapter 章節目錄卡：膠囊標籤 + 標題 + 條列，字直接壓在花紋上
//    reviews 用戶回饋列表：遮罩信箱 + 星等 + 評語
//
//  之後要新的樣式（例如兩欄、或圖上疊字），就多一種 type，
//  在底下的 renderBlock 加一個分支 —— 不要把版型判斷寫進資料表裡。
const IMG = `${process.env.PUBLIC_URL}/images/jingyue/`;

//—— 命盤四乘四的排法 ——
//
//  十二宮在盤上的位置是**地支**決定的，不是宮名 ——
//  「申在右上角」永遠成立，但命宮落在哪一格因人而異。
//  所以版面照這個順序填，再用地支去查那一格是哪一宮。
//
//    巳 午 未 申
//    辰 ┌─────┐ 酉
//    卯 └─────┘ 戌
//    寅 丑 子 亥
const BOARD_ZHI = [
  "巳",
  "午",
  "未",
  "申",
  "辰",
  "酉",
  "卯",
  "戌",
  "寅",
  "丑",
  "子",
  "亥",
];

//  每一格在四乘四裡的 [列, 欄]，兩者都從 1 起算。
//  副星面板只用到列（決定往上還往下長）；欄先留著 ——
//  之後要做「點一下換三方四正的焦點」會需要它。
//  （中宮佔掉第二三列的第二三欄，所以那兩列只有最左與最右有格子。）
const BOARD_POS = {
  巳: [1, 1],
  午: [1, 2],
  未: [1, 3],
  申: [1, 4],
  辰: [2, 1],
  酉: [2, 4],
  卯: [3, 1],
  戌: [3, 4],
  寅: [4, 1],
  丑: [4, 2],
  子: [4, 3],
  亥: [4, 4],
};

//—— 三方四正的連線 ——
//
//  以命宮為起點，連到它的對宮（遷移）與三合（財帛、事業）。
//  一組四條：三條從命宮那個角射出去，一條把另外兩點連起來，成一個三角形。
//
//  座標是中宮那塊空地的百分比（viewBox 0 0 100 100）。
//  數字看起來像魔法但不是：命宮在第幾格就決定了這個三角形的角度，
//  十二種各一組，全部列出來比用三角函數算一遍好懂也好改。
//  （整組照抄元辰那支命盤的 ZIWEI_DIRECTION —— 那邊跑很久了。）
//
//  索引對應 BOARD_ZHI：0=巳 1=午 2=未 3=申 4=辰 5=酉 6=卯 7=戌 8=寅 9=丑 10=子 11=亥
//  四化的 class 名用 ASCII。CSS 的識別字其實吃得下中文，
//  但這個 repo 已經有一條「字型名用 ASCII，非 ASCII 在某些工具鏈裡會被
//  轉碼弄壞」的教訓（見 index.scss）—— class 名沒有必要去賭同一件事。
const HUA_KEY = { 祿: "lu", 權: "quan", 科: "ke", 忌: "ji" };

const BOARD_LINES = [
  [
    { x1: "0", y1: "0", x2: "100", y2: "100" },
    { x1: "0", y1: "0", x2: "100", y2: "25" },
    { x1: "0", y1: "0", x2: "25", y2: "100" },
    { x1: "25", y1: "100", x2: "100", y2: "25" },
  ],
  [
    { x1: "25", y1: "0", x2: "75", y2: "100" },
    { x1: "25", y1: "0", x2: "100", y2: "75" },
    { x1: "25", y1: "0", x2: "0", y2: "100" },
    { x1: "0", y1: "100", x2: "100", y2: "75" },
  ],
  [
    { x1: "75", y1: "0", x2: "25", y2: "100" },
    { x1: "75", y1: "0", x2: "0", y2: "75" },
    { x1: "75", y1: "0", x2: "100", y2: "100" },
    { x1: "0", y1: "75", x2: "100", y2: "100" },
  ],
  [
    { x1: "100", y1: "0", x2: "0", y2: "100" },
    { x1: "100", y1: "0", x2: "0", y2: "25" },
    { x1: "100", y1: "0", x2: "75", y2: "100" },
    { x1: "0", y1: "25", x2: "75", y2: "100" },
  ],
  [
    { x1: "0", y1: "25", x2: "100", y2: "75" },
    { x1: "0", y1: "25", x2: "100", y2: "0" },
    { x1: "0", y1: "25", x2: "75", y2: "100" },
    { x1: "100", y1: "0", x2: "75", y2: "100" },
  ],
  [
    { x1: "100", y1: "25", x2: "0", y2: "75" },
    { x1: "100", y1: "25", x2: "0", y2: "0" },
    { x1: "100", y1: "25", x2: "25", y2: "100" },
    { x1: "0", y1: "0", x2: "25", y2: "100" },
  ],
  [
    { x1: "0", y1: "75", x2: "75", y2: "0" },
    { x1: "0", y1: "75", x2: "100", y2: "100" },
    { x1: "0", y1: "75", x2: "100", y2: "25" },
    { x1: "75", y1: "0", x2: "100", y2: "100" },
  ],
  [
    { x1: "100", y1: "75", x2: "0", y2: "25" },
    { x1: "100", y1: "75", x2: "25", y2: "0" },
    { x1: "100", y1: "75", x2: "0", y2: "100" },
    { x1: "25", y1: "0", x2: "0", y2: "100" },
  ],
  [
    { x1: "0", y1: "100", x2: "100", y2: "0" },
    { x1: "0", y1: "100", x2: "100", y2: "75" },
    { x1: "0", y1: "100", x2: "25", y2: "0" },
    { x1: "25", y1: "0", x2: "100", y2: "75" },
  ],
  [
    { x1: "25", y1: "100", x2: "75", y2: "0" },
    { x1: "25", y1: "100", x2: "100", y2: "25" },
    { x1: "25", y1: "100", x2: "0", y2: "0" },
    { x1: "0", y1: "0", x2: "100", y2: "25" },
  ],
  [
    { x1: "75", y1: "100", x2: "25", y2: "0" },
    { x1: "75", y1: "100", x2: "0", y2: "25" },
    { x1: "75", y1: "100", x2: "100", y2: "0" },
    { x1: "100", y1: "0", x2: "0", y2: "25" },
  ],
  [
    { x1: "100", y1: "100", x2: "0", y2: "0" },
    { x1: "100", y1: "100", x2: "0", y2: "75" },
    { x1: "100", y1: "100", x2: "75", y2: "0" },
    { x1: "75", y1: "0", x2: "0", y2: "75" },
  ],
];

//—— 命書面板的佔位內文 ——
//
//  ⚠️ 暫時的。後端的紫微 teaser 引擎做好之後，這一整段連同各面板的
//  body 欄位一起刪掉 —— 真正的內文是依命盤生成的（bodyFrom）。
//
//  留著它是為了一件事：生成會失敗（限流、安全機制截斷、回來的不是 JSON），
//  而空內文的面板會整塊不掛（見 renderBlock）—— 那幾塊在畫面上等於不存在，
//  使用者會看到兩段內容之間莫名其妙少了一塊。
//
//  四塊面板共用同一段：它是備援不是內容 ——
//  各寫一段不同的假文字只會讓人以為那是定稿。
//  （代價是四塊都失敗時會看到同一段文字重複四次，但那種情況本來就該去看日誌。）
//
//  ⚠️ 這段同時是**語氣的基準**。
//  engines/ziwei-jingyue/jingyue-teaser-engine 的 buildSystem 那套規則是從
//  這五句反推出來的（短句、主詞常是星而不是你、陳述而非質問、句末輕）。
//  改這五句等於改生成的語氣準則 —— 要改的話兩邊一起看。
//
//  跟白夜那份的差別就是整個角色的差別：
//    白夜  「你表面上安靜沉穩得體，骨子裡那把燥火卻快要把自己悶乾了。」
//    鏡月  「你的星，外頭看著是亮的，裡頭卻一點風就晃。」
//  同一個觀察，一個是懶懶地點破，一個是陪著你看。
//  （白夜的語氣 2026-10-14 重新定調過：慵懶、神秘、帶戲謔但深沉。
//  原本引的那句「欸，你…玻璃心炸裂」是舊版，已經作廢。）
const PANEL_PLACEHOLDER = [
  "你的星，外頭看著是亮的，裡頭卻一點風就晃。",
  "每回要做決定，你總在原地站很久，然後把話嚥回去，自己消化。",
  "你要的從來不是更堅強，是有人肯在你不堅強的時候留下來。",
  "嘴上說無所謂，可親近的人一句話，你就碎了一地，再自己默默撿起來。",
  "所以這陣子，你最放不下的是人與人之間那點信任吧。",
];

const BLOCKS = [
  //1. 鏡月在星圖前
  { type: "image", src: `${IMG}jingyue-story-1.jpg` },

  //2. 承上啟下的一句話
  //   給字串就是一般白字，給物件才帶樣式。
  //
  //   gradient 落在第二句：第一句是邀請（看哪裡），第二句才是重音（看什麼）。
  //   兩句合起來是一個完整的句子，主詞是星斗 —— 她沒有把自己放進來，
  //   是星在寫、她只是帶你看。這跟劇情頁第一幕的標語
  //   「我講的，都是天上的星為你寫下的劇本」是同一個母題。
  //
  //   head / tail 兩邊都開（Kevin 2026-10-13）：這一塊夾在兩張整圖中間，
  //   用基本的 50/30 會貼得太緊，看起來像壓在圖上而不是獨立的一段話。
  {
    type: "text",
    head: true,
    tail: true,
    lines: [
      "來看看這漫天星斗",
      { text: "為你寫下了什麼樣的劇本", style: "gradient" },
    ],
  },

  //3. 鏡月托著星盤沉吟。對話框疊在圖上 ——
  //   bubble 的位置與大小都用百分比，圖縮放時框會跟著跑，不會脫節。
  //
  //   位置沿用白夜那組數字（top 18% / left 11%）—— 量過 Kevin 給的參考圖，
  //   框落在 top 18.5% / left 11.0%，兩張圖的構圖剛好對得上
  //   （角色都在右半、左上是空的）。
  {
    type: "image",
    src: `${IMG}jingyue-story-2.jpg`,
    bubbles: [{ text: "嗯…", top: "18%", left: "11%", width: "24%" }],
  },

  //4. 第一個鉤子：問到使用者的痛處。
  //   前兩句是鋪陳（把人帶進那個情境），「卻達不到預期？」才是重音。
  //   gap 讓它和上面空開一段 —— 那個空白就是問號之前的停頓。
  //
  //   size: "lg" 把整段一起放大：這是第一次直接對著讀者說話，
  //   份量要壓過前面那句引導。
  //
  //   head + tail（68 / 60）。
  //
  //   先照參考圖算過一輪：它的上間距 186px、下間距 232px，字高 67px，
  //   換算到我們的字高 28px 是上 78 下 97，扣掉行高留白後 padding 要 70/89，
  //   也就是 head + tail:"lg"。但那樣在真的版面上太空（Kevin 2026-10-13），
  //   所以收回 tail 的 60。
  //
  //   參考圖算出來偏大的原因應該是它的截圖比例：那張圖的字高 67px、
  //   欄寬 1125px，而實際版面是字高 28px、欄寬 393px —— 兩者的字佔欄寬比例
  //   不同，等比例換算出來的留白看起來就會比預期鬆。
  {
    type: "text",
    size: "lg",
    head: true,
    tail: true,
    lines: [
      "你是不是",
      "常覺得自己明明很努力了",
      { text: "卻達不到預期？", style: "jade", gap: true },
    ],
  },

  //5. 鏡月讀生辰。框裡寫的是使用者自己的四柱 ——
  //   這是整頁第一次出現「他的」東西，前面都還是講給任何人聽的開場。
  //
  //   ⚠️ 這裡的四柱走 ziweiPillars 不是 baziPillars：
  //   紫微是**農曆正月初一**換年，八字是立春換年。實測 2024/02/06
  //   （立春後、初一前）八字是甲辰年、紫微是癸卯年 —— 年柱整個不一樣。
  //   打錯端點的話，畫面上秀的年柱會跟報告裡排出來的盤對不起來。
  //
  //   bubble.from 而不是 bubble.text：內容要等排盤回來才有，
  //   但「框長什麼樣、擺在哪裡」仍然是這張表的事（見 renderBlock 的 data）。
  {
    type: "image",
    src: `${IMG}jingyue-story-3.jpg`,
    bubbles: [
      {
        from: "pillars",
        //框靠右下角擺。四個數字都是「相對整張圖」的百分比：
        //  left/width 相對圖寬，top 相對圖高（absolute 的百分比就是這樣算的）。
        //
        //  right  = 54 + 42 = 96%  → 右邊留 4% 的邊
        //  bottom = 60.5 + 32.5 = 93%  → 下面留 7% 的邊
        //  （框高 = 框寬 ÷ aspect = 42% ÷ 0.72 = 58.3% 的「圖寬」，
        //    換算成圖高的百分比要再乘上圖的長寬比 0.558，得 32.5%）
        //
        //  要整體縮放就只改 width，然後把 left/top 依上面兩條式子反推 ——
        //  字級是「框寬 × ratio」，會自己跟著縮，不必另外調。
        top: "60.5%",
        left: "54%",
        width: "42%",
        //字相對框的比例。整體大小交給 width 管，這個只管「字在框裡佔多滿」。
        ratio: 0.17,
        //0.72 是量設計稿得到的（367 x 515px）
        aspect: "0.72",
        //這顆的黑字外圍有一圈青藍光（量到 ΔR −19 / ΔG 0 / ΔB −5）
        glow: true,
      },
    ],
  },

  //6. 叫出對方的名字。整頁到這裡才第一次指名道姓 ——
  //   前面都還是「你」，這一刻他抬頭看著你說出你的名字。
  //   所以這一塊只有一行、字大、四周空，不放任何別的東西。
  {
    type: "text",
    //tail：名字唸完要留一段空白，下一塊的眼睛才不會馬上壓上來
    tail: true,
    lines: [{ from: "name", style: "name" }],
  },

  //7. 抬眼。只有一雙眼睛的橫幅特寫 ——
  //   名字被叫出來之後，接的是她看過來的那一眼，不需要任何文字。
  //   這張是橫式（877×458，比例 1.91），在滿版的直欄裡只佔一條窄帶，
  //   上下的淡出會讓它像從黑裡浮出來又沉回去。
  { type: "image", src: `${IMG}jingyue-story-4.jpg` },

  //8. 她開口接話。四句都用漸層字（同 block 2 的「為你寫下了什麼樣的劇本」）——
  //   那是「帶你往下看」的語氣，跟 block 4 靛藍發光的那一句不是同一種。
  //
  //   前三句是一句話拆成三行，不是三句 ——「莫怕／而是…／為你寫下的劇本」
  //   連起來才完整。拆行是為了讓「星」與「劇本」各自落在行尾被看見。
  //
  //   第三行回扣第一幕輪播的「我講的，都是天上的星為你寫下的劇本」——
  //   那是她進場時說的話，到這裡才兌現，整條動線在這裡閉第一個環。
  //
  //   中間夾一條直線：上下是同一個人連著講的話，她只是頓了一下，
  //   所以不能用橫線（那會切成兩個段落）。
  {
    type: "text",
    //sm：這幾句比前面長（最長一行十三個字），用轉折點的字級會擠到貼邊
    size: "sm",
    //  收尾要停一下。量設計稿：最後一行到下一塊圖之間約 60px（圖是二倍，
    //  扣掉行框的下伸）—— 正好是 --tail 的值，不必另開一級。
    //  這一句是整頁的鉤子（CTA 也是在這裡出現的），
    //  緊接著就把下一張圖推上來的話，那個問句還沒落地就被蓋掉了。
    tail: true,
    lines: [
      { text: "莫怕 這不是你不夠好", style: "gradient" },
      { text: "而是你還沒看懂天上的星", style: "gradient" },
      { text: "為你寫下的劇本", style: "gradient" },
      { divider: true },
      {
        //⚠️ 十三個字，是這一塊最寬的一行。量過（Noto Serif TC 25px）：
        //   行寬 325px，而文字 block 的內寬是「欄寬 − 48」。
        //   361–372px 的視窗內寬只有 313–324px → 會折成兩行。
        //   360px 以下不受影響（字級掉到 21px，行寬 273px，內寬 280px）。
        //   要再加字的話先回來量一次。
        text: "你是為了「那件事」來的吧？",
        style: "gradient",
        //滑到這一句，底部的按鈕就出現
        id: CTA_TRIGGER_ID,
      },
    ],
  },

  //9. 她攤開手，一道星芒浮在掌心。框裡兩句是她說出口的話 ——
  //   跟 block 3 的「嗯…」一樣是說出口的話，所以同樣放在對話框裡，
  //   而不是做成黑底的文字 block（那是旁白的位置）。
  //
  //   這顆框的下緣落在圖高的 112%：刻意掛出圖外，讓它壓在下一塊的黑底上，
  //   像一張被放到畫面前面的紙。溢出能成立是因為 __bubble 有 z-index（見 scss）。
  {
    type: "image",
    src: `${IMG}jingyue-story-5.jpg`,
    bubbles: [
      {
        text: "牽著\n這根星芒",
        //  —— 這四個值是「擬合」出來的，不是量端點 ——
        //  橢圓的上半截疊在人像上，顏色被底下的圖染過，抓不到邊；
        //  下半截才落在純黑上（填色固定是 rgb(158,158,158)）。
        //  所以只取 y>1230 那幾列的半寬，去解 x²/a² + y²/b² = 1，
        //  再回推上緣。殘差 9px（圖是二倍，約 4px），夠用。
        //
        //  直接拿「看得到灰色的第一列」當上緣會差很多 ——
        //  試過，會偏低 160px，框整個掉下去蓋住她的手。
        top: "67.9%",
        left: "28.0%",
        width: "43.6%",
        //兩行裡最長的是「這根星芒」四個字。
        //  ratio 不是估的：量設計稿裡字與字的間距是 57.7px，
        //  而 bubble-text 有 0.03em 字距 → 字級 57.7 ÷ 1.03 = 56px，
        //  框寬 390px ⇒ 56 / 390 = 0.144。
        ratio: 0.144,
        //  0.72 而不是量到的 0.65 —— 刻意把框壓短一成。
        //  量設計稿是 390×600，照那個比例在真機上顯得過長；
        //  框高 = 框寬 ÷ aspect，所以調大 aspect 就是壓低它。
        //  ⚠️ 動了 aspect 一定要跟著重算下面的 spill，不然框底與
        //  下一塊的留白會對不上（算式就寫在那裡）。
        aspect: "0.72",
      },
    ],
    //框的下緣落在圖高的 107.8%，也就是掛出去 7.8% 的圖高。
    //  換算成圖寬：7.8% × 1.5178（這張圖的高寬比）≈ 11.8%。
    //  算式：框底 = top + 框高 = 67.9% + (43.6% ÷ 0.72 ÷ 1.5178) = 107.8%
    spill: "11.8%",
  },

  //10. 接在星芒之後的兩句。第二句加重 ——
  //    前一句是邀請（抬頭看），後一句才是她要你去看的那件事，
  //    所以只有後面那句發光。
  //
  //    —— 為什麼不用 size: "sm" ——
  //    量設計稿：字與字的間距 57.4px（圖是二倍），即字級約 28px，
  //    不是 --sm 的 25px。改用逐行的 is-md（28px / 小螢幕 23px）：
  //    .jingyue-story__line.is-md 與 .--sm .jingyue-story__line 同為 0,2,0，
  //    靠的是 is-md 寫在後面（見 scss 裡那段註解）。
  //
  //    第二句再往上一級到 lg（30px）。設計稿上兩句是同一個字級，
  //    這是刻意拉開的 —— 前一句是邀請，後一句才是鉤子，
  //    兩句同大小的話那個問句沒有壓過前一句。
  //
  //    head：上一塊的框掛出圖外 11.8%，落點就在這一塊的黑底上。
  //    不留這段空白的話，第一句會貼著那顆框的下緣。
  {
    type: "text",
    head: true,
    //tail "lg"（90px，原本是 60px）：這句說完就進命書了，中間要有一段空白當換場。
    //  空白加在這一塊的下緣、而不是面板的 --head ——
    //  面板那個旋鈕最小一級是 90px，一次就把間距從 120px 推到 210px，
    //  而它存在的理由是「讓開一顆掛很出去的對話框」（見 scss 裡那段註解），
    //  這裡沒有那種溢出。往上調一級 tail 只多 30px，剛好。
    tail: "lg",
    lines: [
      { text: "看看這漫天星斗", style: "md" },
      //gap "sm"：邀請完停一下，但兩句還是連著的，不到另起一段的程度
      //
      //  glow 而不是只有 strong：量 Kevin 的參考圖（黑底，以字緣為原點
      //  做距離變換、一圈一圈取平均），跟兩種光各自實渲染一次比對 ——
      //
      //              離字1-2      3-5         6-9        10-16
      //    參考圖    (76,78,109) (34,36,62) (19,20,35) ( 7, 7,13)
      //    is-glow   (72,75,102) (31,33,55) (20,22,36) (10,10,17)  ← 十二個通道平均差 3.4
      //    is-strong (54,55, 62) (14,15,21) ( 9, 9,13) ( 4, 4, 6)  ← 中距離差一倍以上
      //
      //  差一倍不是量測誤差的量級。這句要的是「這句話在發亮」，
      //  不是 .is-strong 那種「字下壓著一道影」。
      { text: "究竟藏著什麼秘密", style: "strong glow lg", gap: "sm" },
    ],
  },

  //11. 命書面板。整頁第一次出現大段內文 ——
  //    前面都是一句一句的台詞，到這裡才真的開始「批命」。
  //
  //    bodyFrom 而不是把文字寫在這裡：這段是要用 AI 依命盤生成的。
  //
  //    body 是**備援**不是常態：引擎已經接上了
  //    （engines/ziwei-jingyue/jingyue-teaser-engine 與 -gemini），
  //    正常情況四塊都會拿到 AI 生的內容。
  //    但生成會失敗（限流、安全機制截斷、回來的不是 JSON），
  //    而 renderBlock 碰到空內文會整塊不掛 —— 那一塊在畫面上等於不存在。
  //    留著這段替身，失敗時至少版面是完整的。四塊共用同一個常數。
  {
    type: "panel",
    bg: `${IMG}jingyue-story-bg-1.jpg`,
    title: "你現在的狀況是",
    bodyFrom: "situation",
    body: PANEL_PLACEHOLDER,
  },

  //12. 她把原因歸到命局上。前兩句是鋪陳（一般字重），
  //    第三句是結論：加粗、不發光（flat），並把「命局」兩個字挑出來換色 ——
  //    整段話的重量都壓在那兩個字上，所以整行只有它們亮。
  //
  //    —— 三行都用 fit，不是固定字級 ——
  //    量設計稿（欄寬 440）：前兩句的字距是 57.5px（圖是二倍），即 28px。
  //    但第二句有十三個字，28px 要 364px 的可用寬，而文字 block 的內寬
  //    是「視窗 − 48」—— 412px 以上才不折行，390／393／414 全部中招。
  //
  //    is-fit 的字級是 min(fit, 可用寬 ÷ 字數)：寬螢幕就是設計稿的 28px，
  //    窄螢幕才開始縮，而且永遠不折行（見 scss 裡那段說明）。
  //    字數由 renderLine 自己數，不寫在表裡。
  //
  //    三行都掛 —— 只給第二行的話，窄螢幕上同一句話會出現兩種字級。
  {
    type: "text",
    //格式比照 block 10（看看這漫天星斗／究竟藏著什麼秘密）——
    //  那兩塊是同一種語氣：前面鋪陳，最後一句加重把話說死。
    //
    //  head／tail：這一塊前面是命書面板、後面是整張命盤，兩邊都是
    //  有邊框的大塊面。夾在中間的三行字要有足夠的留白才站得住 ——
    //  用預設的 50／30 時它看起來像黏在面板底下的附註。
    //  實際的縫還要加上那兩塊自己的內距（面板 60px、命盤 56px）。
    head: true,
    tail: true,
    lines: [
      { text: "看到這裡", style: "fit", fit: "28px", fitSm: "23px" },
      {
        text: "終於明白你為何過得這麼累了",
        style: "fit",
        fit: "28px",
        fitSm: "23px",
      },
      {
        parts: ["全是你的", { text: "命局" }, "寫好的"],
        //flat：加重但不發光。
        //  strong 本來帶一片往下沉的光（見 scss 的 .is-strong），
        //  但這一行裡「命局」兩個字已經是靛紫、自己就是視覺重點 ——
        //  底下再墊一層光會跟它撞色，兩個亮點在同一行搶。
        //  白字保留粗體當底，讓那兩個字獨自發亮。
        style: "strong flat fit",
        fit: "28px",
        fitSm: "23px",
        //"lg"（1.6em）。原本照設計稿量出來是 2.4em（xl），
        //  但實際擺進頁面之後那一段空白太開 —— 前兩句與這一句會讀成
        //  兩段話，而它們是同一口氣：前面鋪陳，這一句把話說死。
        //  （量設計稿會偏大的原因同 block 4：設計稿的字高與欄寬比例
        //    跟實際版面不同，照比例換算出來的留白會一路偏大。）
        gap: "lg",
      },
    ],
  },

  //13. 命盤。整頁第一次給「資料」而不是話術 ——
  //    前面講的都是她的判斷，這裡是排出來的盤，讓人自己看。
  //    上一塊剛說完「全是你的命局寫好的」，這裡就把那張命局攤開。
  //
  //    內容全部來自後端（from: "board"），前端不做任何命理換算：
  //    星曜、廟旺、四化、大限、五行局都是排盤的一部分，
  //    在這裡重算等於維護第二套實作（見 CLAUDE.md「排盤不要在前端算」）。
  //
  //    背景用 jingyue-story-bg-1：跟命書面板同一張星圖花紋。
  //    這一塊與那四塊是同一種東西（「她給你看的東西」），
  //    各配一張背景會讓它們看起來像來自兩份文件。
  {
    type: "board",
    bg: `${IMG}jingyue-story-bg-1.jpg`,
    from: "board",
  },

  //14. 她指尖的星芒轉起來，然後問你敢不敢看。兩顆對話框：
  //    左邊那顆是她起手時說的，右邊那顆是反問 —— 位置錯開、右邊那顆更低，
  //    讀起來才有先後，而不是同時講的兩句話。
  //
  //    右邊那顆幾乎整顆都在圖片之外（top 96.4%，框底落在圖高的 179%）。
  //    這是刻意的：星芒在圖裡，問句落在圖外的黑底上，
  //    像她把那句話丟到畫面前面來。
  {
    type: "image",
    src: `${IMG}jingyue-story-6.jpg`,
    bubbles: [
      {
        text: "指尖星芒\n已動…",
        //  —— 這幾個值是擬合出來的，不是量端點 ——
        //  框的上半截疊在人像上、顏色被底下的圖染過，抓不到邊；
        //  下半截才落在純黑上（填色固定 rgb(203,203,203)）。
        //  所以只取那幾列的半寬去解橢圓，再回推上緣。殘差 2.1px
        //  （圖寬 900，約 0.23%）。作法同 block 9，那裡有完整說明。
        top: "66.4%",
        left: "11.7%",
        width: "27.9%",
        //字級 = 框寬 × ratio。量設計稿：字距 53px、框寬 251px ⇒ 0.211。
        //  比右邊那顆大一截 —— 這句短（最長四個字），框也小，
        //  用同一個 ratio 的話字會縮成一團。
        ratio: 0.211,
        //0.75：量設計稿是 0.810，這裡刻意再拉長一成 ——
        //  框高 = 框寬 ÷ aspect，所以調小 aspect 就是把它拉長。
        //  top 不動，所以是往下長：上緣跟她的手維持原本的關係。
        //
        //  框底 = 66.4% + (27.9% ÷ 0.75 ÷ 0.6857) = 120.7% 的圖高，
        //  仍然比右邊那顆的 179.2% 高，所以下面的 spill 不用改。
        aspect: "0.75",
      },
      {
        text: "你敢不敢\n窺見自己的\n起伏定數？",
        top: "96.4%",
        left: "45.1%",
        width: "41.7%",
        //量設計稿：字距 58.5px、框寬 376px ⇒ 0.156。
        //  三行裡最長的是「窺見自己的」五個字，ratio 要遷就它。
        ratio: 0.156,
        aspect: "0.734",
        //這顆的黑字外圍有一圈靛紫光。量設計稿（以填色 180 為基準）：
        //  離字 1-2px (−26.6, −25.0, +5.8)、3-5px (−11.2, −9.8, +14.1)
        //  R 與 G 一起掉、B 反而升 —— 跟 block 5 那顆四柱框同一種光。
        glow: true,
      },
    ],
    //兩顆框裡最低的是右邊那顆，下緣落在圖高的 179.2%。
    //  算式：框底 = top + 框高 = 96.4% + (41.7% ÷ 0.734 ÷ 0.6857) = 179.2%
    //  （0.6857 是這張圖的高寬比 1082/1578）
    //
    //  要讓出的高度 = (179.2% − 100%) 的圖高，換算成圖寬要再乘回 0.6857：
    //  79.2% × 0.6857 ≈ 54.3%。（percentage margin 一律以包含區塊的寬度為基準）
    //
    //  兩顆框縮放或上下移動時，這個數字都要跟著重算 ——
    //  掛出去那一截的長度會變，不改的話下面會多出（或少掉）一段空白。
    spill: "54.3%",
  },

  //15. 承上的旁白。兩句連著唸，第二句往下淡出 ——
  //    上一塊問完「你敢不敢窺見」，這兩句是她替你把答案說了。
  //
  //    第二句用 fade 不用 gradient：量設計稿那一行是**中性灰**
  //    （R=G=B，由 206 降到 15），不是 .is-gradient 那道白→靛紫。
  //    兩者的語氣不同 —— 漸層是「帶你往下看」，淡出是「說完就沉進夜裡」。
  //
  //    兩行都 md（28px）。量設計稿：字距 57.3px（圖是二倍）。
  //    第二句十個字 × 28px = 280px，375px 的視窗內寬 327px，不用 fit。
  {
    type: "text",
    //head "lg"（88px，預設是 50px）：上一塊那顆問句的框掛出圖外很長
    //  （spill 54.3%），落點就在這一塊的黑底上。
    //  不把這兩句再往下推的話，它們會像是黏在那顆框底下的註解，
    //  而不是另一個人在另一個時刻開口。
    head: "lg",
    //tail "lg"（90px，原本 60px）：說完才抬眼，下一張特寫不要緊接著上來。
    //  這一塊的最後一行是往下淡出的（.is-fade 收在接近純黑），
    //  空白不夠的話那道淡出還沒沉完，下一張圖就撞上來了。
    //
    //  空白加在這裡、不是下一張圖的上緣 ——
    //  這一頁的圖與圖之間要無縫接起來（見 scss 的 &__blocks）。
    tail: "lg",
    lines: [
      { text: "看清了", style: "md" },
      //不加 gap：量設計稿兩行的字頂相距 83px（圖是二倍）＝ 40.6px，
      //  就是一個行框（28 × 1.5 = 42）。兩句是連著唸的，中間不停。
      { text: "這顆星正為你劃破長夜", style: "fade md" },
    ],
  },

  //16. 再一次的眼睛特寫，比 block 7 更近 ——
  //    說完「這顆星正為你劃破長夜」之後，接的是她看過來的那一眼。
  //    同樣不配任何文字：這一刻該安靜。
  //
  //    ⚠️ 這張的比例跟白夜那張不一樣（1.20 vs 1.46），所以它會高一截：
  //    在 440px 的欄寬下是 367px，白夜那張是 301px。
  //    純圖片 block 沒有對話框也沒有 spill，不必重算任何東西 ——
  //    但下一塊的留白是照「上一塊收在哪裡」調的，換圖之後值得再看一眼。
  { type: "image", src: `${IMG}jingyue-story-7.jpg` },

  //17. 她把這張盤的讀法講一遍。兩段，中間用一條白線換場：
  //    上面是「盤是怎麼構成的」（十四主星配十二宮位），
  //    下面是「所以它說了什麼」（轉折點與大限機緣）。
  //
  //    分隔線用白色而不是 block 8 那條靛紫：那條是同一段話中間的停頓，
  //    這條是換場 —— 上下是兩種不同的語氣。
  //
  //    —— 為什麼拿掉 size: "sm"，改成逐行給 ——
  //    這一塊有四種字級（28／40／33／35）。留著 --sm 的話，
  //    「十二宮位」那一行吃不到 is-xl：.is-gradient 在 --sm 區塊裡的
  //    那條規則是 0,3,0，壓得過逐行的 0,2,0。改成逐行就沒有這個問題。
  {
    type: "text",
    lines: [
      { text: "紫微十四主星", style: "md" },
      { text: "各司其職", style: "md" },
      //漸層字，而且是整塊最大的一行（40px）——
      //  上面兩句是鋪陳，這四個字才是主詞。
      { text: "十二宮位", style: "gradient xxl", gap: true },
      { divider: "plain" },
      //兩句連著唸（不加 gap），第二句把兩個詞挑出來換成靛紫。
      //
      //  「轉折點」與「大限機緣」是這一段的落點 ——
      //  它們沿用 __mark 的顏色，光暈則是繼承 strong 的（mark 自己不發光），
      //  所以整行是同一種光，不會變成兩個亮點在搶。
      //這一句比下一句小一階（33 vs 35）—— 它是鋪陳，
      //  「轉折點與大限機緣」才是落點。同大小的話兩句會讀成並列的兩件事。
      //  差距刻意只有 2px：30px（lg）試過，壓得太扁，變成一句附註。
      { text: "刻劃著你這輩子的", style: "strong xl-soft" },
      {
        parts: [{ text: "轉折點" }, "與", { text: "大限機緣" }],
        style: "strong xl",
      },
    ],
  },

  //18. 章節大標：大限。整頁到這裡換段 ——
  //    前面是她跟你的對話，從這裡開始是一章一章的批命。
  //
  //    ⚠️ 是「大限」不是「大運」——「大運」是八字的說法，
  //    紫微的十年一步叫大限（排盤器回的欄位名也是 decades/大限）。
  //    所以版型也換：一條飾紋 + 兩個巨大的字，不講話。
  {
    type: "text",
    //head "sm"（24px，預設是 50px）：飾紋圖的線條在正中央，
    //  上下各有約 64px 的空白是畫在圖裡的 —— 再疊預設的 50px，
    //  從上一塊的字到那條線會隔 144px，中間空一大片。
    head: "sm",
    //tail "lg"：章節標題要四周空，不能讓下一張圖貼著「大限」兩個字
    tail: "lg",
    lines: [
      //  ⚠️ 這張飾紋是 **jpg**，不是像白夜那張一樣的透明 png。
      //  它的黑底是實心的（量過四角是 rgb(0,0,0)），而這一塊的底色是
      //  $c-bg rgb(10,10,15) —— 直接疊上去會出現一條看得出來的黑帶。
      //  解法在 scss：&__ornament 用 mix-blend-mode: screen，
      //  純黑在 screen 下等於透明，所以效果跟透明 png 一樣。
      //  三個章節大標（大限／財運／愛情）共用同一張。
      { ornament: `${IMG}jingyue-story-bg-2.jpg` },
      //兩個字寫在同一筆裡（用 \n 分行）——
      //  淡出的漸層是套在「大限」整體上的，拆成兩行會各自套一次
      { title: "大\n限" },
    ],
  },

  //19. 進入大限章的第一段。兩顆框由上而下、由左而右 ——
  //    上面那顆是她起話頭（你現在正處於），下面那顆才是結論，
  //    位置錯開才有「頓一下再說」的先後。
  //
  //    兩顆框的幾何都是擬合出來的（殘差 2.6 / 2.2px，圖寬 898）：
  //    上半截疊在人像上、顏色被底圖染過抓不到邊，只取落在純黑上的
  //    那幾列去解橢圓再回推上緣。作法同 block 9，那裡有完整說明。
  {
    type: "image",
    src: `${IMG}jingyue-story-8.jpg`,
    bubbles: [
      {
        text: "你現在\n正處於",
        top: "51.2%",
        left: "9.6%",
        width: "29.4%",
        //量設計稿：字距 58px、框寬 264px ⇒ 0.220。
        //  比下面那顆大一截 —— 這句短（一行三個字），框也小。
        ratio: 0.22,
        aspect: "0.709",
      },
      {
        text: "人生關鍵的\n大限轉折期",
        top: "77.8%",
        left: "42.2%",
        width: "44.9%",
        //兩顆框的字其實一樣大（都是 58px），差別只在框寬：
        //  58 ÷ 403 = 0.144。ratio 是「字佔框多滿」，不是絕對字級。
        ratio: 0.145,
        //0.70：量設計稿是 0.837，這裡刻意再拉長 (0.837/0.70−1) ≈ 兩成。
        //  框高 = 框寬 ÷ aspect，所以調小 aspect 就是把它拉長。
        //  top 不動，所以是往下長 —— 上緣跟她的手維持原本的關係，
        //  多出來的那一截掛到圖外（下面的 spill 已經跟著重算）。
        aspect: "0.70",
        //這顆的黑字外圍是靛紫光，不是白夜那顆的綠。
        //  量設計稿（框內填色 180 為基準）：
        //    離字 1-2px (−25.0, −23.4, +15.9)、3-5px (−10.8, −9.3, +25.0)
        //  R 與 G 一起掉、B 反而升 —— 同 block 5 與 block 14 那兩顆。
        glow: true,
      },
    ],
    //下框的下緣落在圖高的 113.8%。
    //  算式：框底 = top + 框高 = 77.8% + (44.9% ÷ 0.70 ÷ 1.7806) = 113.8%
    //  （1.7806 是這張圖的高寬比 1136/638）
    //
    //  要讓出的高度 = 13.8% 的圖高，換算成圖寬要再乘回 1.7806 ⇒ 24.6%。
    //  兩顆框上下移動或改 aspect 時，這個數字都要跟著重算
    //  （同 block 14 的說明）。上框的框底是 74.5%，還在圖內，不影響。
    spill: "24.6%",
  },

  //20. 大限章的命書面板。結構同 block 11，差別只在內容與背景 ——
  //    標題兩行（用 \n 分行），內文一樣是之後要由 AI 生成的。
  {
    type: "panel",
    //head：上一塊那顆對話框的下緣剛好落在這裡，要讓開
    head: true,
    //背景跟 block 11 同一張星圖花紋 —— 四塊命書面板是同一種東西
    //  （「她給你看的東西」），各配一張背景會讓它們看起來像來自兩份文件。
    bg: `${IMG}jingyue-story-bg-1.jpg`,
    title: "你的命運\n原來是這樣轉動的",
    //  daXian 不是 daYun：大運是八字的，紫微叫大限。
    //  這個鍵要跟 jingyue-teaser-engine 的 PANELS 一致 ——
    //  對不上這一塊就是空的，而且不會報錯（見 renderBlock 的 data?.[bodyFrom]）。
    bodyFrom: "daXian",
    //生成失敗時的備援（同 block 11）
    body: PANEL_PLACEHOLDER,
  },

  //21. 面板之後的收尾。兩句同字級，第二句淡得更深 ——
  //    聲音在退：她講完那一段批文，話就散進夜裡了。
  {
    type: "text",
    //上下都要留 —— 上面接命書面板、下面接下一章，兩邊都該喘一口。
    //
    //  tail 維持 true（60px）。試過 "lg"（90px），Kevin 說太開了。
    //  那個縫實際上不只 60：還要加章節大標自己的 padding-top 50px，
    //  以及飾紋圖上半那 64px 的空白（畫在圖裡的，改不掉）——
    //  合計 174px，所以 tail 不必再往上加。
    head: true,
    tail: true,
    lines: [
      { text: "這顆紫微星", style: "md" },
      //跟上一句同字級（都是 md 28px）—— 量你的參考圖兩行的字距都是
      //  57.5px（圖是 2.057 倍）。「聲音在退」改由 fade-dim 單獨承擔，
      //  不再靠縮小一級：兩件事一起做，第二句會弱到讀不出來。
      { text: "為你閃爍了千年", style: "fade-dim md" },
    ],
  },

  //22. 第二個章節大標：財運。結構與 block 18 的「大限」完全相同 ——
  //    同一條飾紋、同一種三層疊字，只換兩個字。
  //    章節標題長一樣是刻意的：它是這一頁的分隔記號，
  //    每章各設計一款的話，讀者就沒辦法一眼認出「又換章了」。
  {
    type: "text",
    tail: "lg",
    lines: [{ ornament: `${IMG}jingyue-story-bg-2.jpg` }, { title: "財\n運" }],
  },

  //23. 財運章的第一段。兩顆框，上輕下重 ——
  //    「那麼…」是引子（小框、一行），
  //    「財運如何」才是主題（大框、四個字排滿）。
  //
  //    兩顆框的幾何是擬合出來的（殘差 0.4 / 1.4px，圖寬 900）——
  //    上半截疊在人像上抓不到邊，只取落在純黑上的那幾列去解橢圓。
  //    作法同 block 9，那裡有完整說明。
  //
  //    ⚠️ 這一塊**沒有 spill**。量設計稿：下框的框底落在圖高的 100.1%，
  //    等於剛好收在圖的下緣、沒有掛出去。
  //    框往下移或 aspect 調小的話就要把 spill 加回來，算式見 block 19。
  {
    type: "image",
    src: `${IMG}jingyue-story-9.jpg`,
    bubbles: [
      {
        text: "那麼…",
        top: "45.4%",
        left: "9.5%",
        width: "35.2%",
        //量設計稿：字距 57px、框寬 317px ⇒ 0.180
        ratio: 0.18,
        //0.915：這顆接近圓形，比下面那顆扁得多。量出來的，不是套同一個值。
        aspect: "0.915",
      },
      {
        text: "財運如何",
        top: "67.5%",
        left: "43.2%",
        width: "42.8%",
        //量設計稿：字距 67px、框寬 385px ⇒ 0.174。
        //  字其實比上面那顆大（67 vs 57），ratio 反而小 ——
        //  ratio 是「字佔框多滿」，不是絕對字級（同 block 19 的說明）。
        ratio: 0.174,
        aspect: "0.739",
      },
    ],
  },

  //24. 承上的邀請。靛紫粗體、底下壓著一道往下沉的影
  //    （量設計稿：字身 rgb(140,146,255)，影往上 3px 就歸零、往下 27px）。
  //
  //    上一塊問完「財運如何」，這一句不是再追問，是她把話接過去 ——
  //    「來看星辰是怎麼說的吧？」把視線從你身上移到天上，
  //    那是鏡月一貫的做法（同 block 8 的「而是你還沒看懂天上的星」）。
  {
    type: "text",
    size: "sm",
    //head "lg"／tail "xl"：上下都要留一大拍 ——
    //  這一句是財運章的轉身（從問你、變成帶你去看），
    //  前後都該安靜一下。
    head: "lg",
    tail: "xl",
    lines: [
      //  ⚠️ 不能用 "jade-strong md"（30px）—— 實測 375px 的機器上
      //  行寬 330px、可用寬只有 327px，剛好差 3px 就折行了。
      //  改用 fit：上限一樣是 30px，窄螢幕才自己縮（見 scss）。
      //  也不能寫 md，那一條在 .is-jade-strong 裡是 0,3,0，壓得過 is-fit。
      {
        text: "來看星辰是怎麼說的吧？",
        style: "jade-strong fit",
        fit: "30px",
        fitSm: "25px",
      },
    ],
  },

  //25. 她低頭笑了的特寫。同 block 7／16，不配文字 ——
  //    「來看星辰是怎麼說的吧？」說完之後的那個表情本身就是回答。
  //
  //    比例跟白夜那張幾乎一樣（1.458 vs 1.459），版型沒有連帶要改的。
  { type: "image", src: `${IMG}jingyue-story-10.jpg` },

  //26. 翻開財運那一頁之前的鋪陳。兩句連著唸 ——
  //    「萬物循理」回扣劇情頁第三幕的「萬物皆有定數」：
  //    她不講「我要告訴你」，她講的是本來就在那裡的秩序。
  //
  //    兩句同字級、也不加 gap。
  //    量設計稿是 25px（字距 52.5px、圖是 2.045 倍），這裡用預設的 30px ——
  //    設計稿的值在實際版面上偏小，整塊壓不住上下兩段的留白。
  //
  //    ⚠️ 不要寫 size: "md"。文字 block 只有 --sm 這一級，沒有 --md ——
  //    寫了不會報錯，只會悄悄掉回預設值。要 28px 得逐行給 is-md。
  //
  //    最長的一句十個字 × 30px = 300px，最窄的 375px 視窗內寬 327px，不會折
  //    （實測六種寬度都是一行）。
  {
    type: "text",
    //head "lg"（88px，原本 68px）：跟上面那張特寫拉開，
    //  讓那個表情先停一下。上一塊是純圖片、不帶下外距，
    //  所以這個縫完全由這裡決定。
    head: "lg",
    //tail "xl"（130px，原本 90px）：說完這句才翻開命書，
    //  中間留一大段空白當換場。
    //  下一塊是命書面板，它自己還有 110px 的內距（那一段是花紋不是黑）——
    //  所以純黑的縫是 130px，花紋從那之後才開始。
    tail: "xl",
    lines: ["萬物循理", "屬於你的財富時機已至"],
  },

  //27. 財運章的命書面板。結構同 block 11／20 ——
  //    每一章都是「大標 → 對話 → 面板」這個節奏，面板是那一章的正文。
  //
  //    標題拿掉了「曾」：紫微這一章講的是大限走到這一步的財，
  //    不是只講過去 —— 所以鍵也從白夜的 caiYunPast 改成 caiYun，
  //    引擎那邊這一塊的五拍也跟著拿掉了過去式。
  {
    type: "panel",
    //背景跟 block 11／20 同一張星圖花紋 —— 四塊命書面板是同一種東西，
    //  各配一張背景會讓它們看起來像來自兩份文件。
    bg: `${IMG}jingyue-story-bg-1.jpg`,
    title: "你的財運\n是這樣流轉的",
    bodyFrom: "caiYun",
    //生成失敗時的備援（同 block 11／20）
    body: PANEL_PLACEHOLDER,
  },

  //28. 看完財運之後的結語。三行三種語氣：
  //    第一句是斷言（粗體白字、不發光），第二句收掉（往下淡出），
  //    停一大拍之後才是那句落點（粗體白字 + 靛紫光暈）。
  //
  //    整塊只有最後那句會發光 ——
  //    光在這一塊裡是「這就是重點」的記號，不是加重的通用手段。
  //    前面也亮的話就分不出哪一句才是結論。
  //
  //    三句都是 33px（量設計稿字距 67px、圖是 2.045 倍）。
  //    比白夜那版（30／30／28）大一級，而且三句同大小 ——
  //    語氣的層次全交給顏色與光，不靠字級。
  {
    type: "text",
    //head "lg"：上一塊是命書面板，它的下緣是花紋 ——
    //  這一段黑要夠長，那三句才像換了一個場，不是面板的註腳。
    head: "lg",
    //tail "lg"：這句講完就換章了，中間要空出一段
    tail: "lg",
    lines: [
      //flat：加重但不發光（理由見上方）
      { text: "星軌聚財", style: "strong flat xl-soft" },
      //fade：量設計稿這一句最亮處是 193（中性灰），是往下淡出不是變色。
      //  （fade 本身就不能有 text-shadow，所以這句不必另外加 flat）
      { text: "金芒正在夜空中綻放", style: "strong fade xl-soft" },
      //整塊唯一發光的一句 —— 不加 flat 是刻意的。
      //
      //  glow：比 .is-strong 亮一級。量設計稿，這一句的光在離字 3-5px
      //  處是 block 17 那句的 2.4 倍 —— 那句是「字下壓著影」，
      //  這句是「在發亮」，所以不共用（見 scss 的 .is-glow）。
      //
      //  ⚠️ 用 fit 不用 xl-soft：十個字 × 33px = 330px，
      //  而 375px 的視窗可用寬只有 327px，剛好差 3px 折行（同 block 24）。
      //  fit 的上限一樣是 33px，窄螢幕才自己縮。
      {
        text: "此時正是你破局引財之際",
        style: "strong glow fit",
        fit: "33px",
        fitSm: "27px",
        gap: "lg",
      },
    ],
  },

  //29. 第三個章節大標：愛情。同 block 18（大限）、block 22（財運）。
  {
    type: "text",
    tail: "lg",
    lines: [{ ornament: `${IMG}jingyue-story-bg-2.jpg` }, { title: "愛\n情" }],
  },

  //30. 愛情章的第一段。兩顆框斜對角擺 ——
  //    上框在左上、下框在右下，視線從左上掃到右下，剛好經過她的臉。
  //
  //    ⚠️ **這一塊的垂直位置是估的，不是量的。**
  //    參考圖裡那張人像的比例跟 jingyue-story-11.jpg 對不上
  //    （mock 的圖高約是欄寬的 2.03 倍，實際素材是 1.79 倍），
  //    所以 top 是用「框在圖上的相對位置」換算過來的，不是直接量到的值。
  //    真的擺上去之後八成要微調 —— 要調就只動 top，spill 跟著下面的算式重算。
  //
  //    橫向與大小是可靠的（框寬、aspect、字級都量得到）。
  {
    type: "image",
    src: `${IMG}jingyue-story-11.jpg`,
    bubbles: [
      {
        text: "情線\n如星軌",
        //這顆比其他框實（0.81 vs 預設 0.7）——
        //  它壓在畫面最亮的那一角（燈籠與星軌），照預設的濃度黑字會糊掉。
        //  量設計稿：框內在黑底上是 rgb(203,203,203) ⇒ 203/250 = 0.81。
        fill: 0.81,
        //0.5%：幾乎貼齊圖的上緣。白夜那版是 -9.2%（掛到圖外），
        //  鏡月這張的框整顆都在圖裡。
        top: "0.5%",
        left: "6.3%",
        width: "36.2%",
        //量設計稿：字距 58px、框寬 326px ⇒ 0.178
        ratio: 0.178,
        aspect: "0.797",
      },
      {
        text: "有些相遇\n本就是\n命中注定",
        top: "67.4%",
        left: "42.5%",
        width: "49.4%",
        //量設計稿：行距 96px ⇒ 字級 64px，框寬 445px ⇒ 0.144。
        //  三行裡最長的是四個字，所以框裡還留得住白。
        ratio: 0.144,
        aspect: "0.786",
        //靛紫光，同 block 5／14／19 那幾顆
        glow: true,
      },
    ],
    //下框的下緣落在圖高的 102.5%。
    //  算式：框底 = top + 框高 = 67.4% + (49.4% ÷ 0.786 ÷ 1.7911) = 102.5%
    //  （1.7911 是這張圖的高寬比 2006/1120）
    //  要讓出的高度 = 2.5% 的圖高 × 1.7911 ≈ 4.5%。
    //  上框的框底是 25.9%，還在圖內，不影響。
    spill: "4.5%",
  },

  //31. 愛情章的命書面板。第四塊了，結構完全沒變 ——
  //    標題這次只有一行，用不用 \n 是內容的事，版型不受影響。
  //
  //    標題從「我已瞧見那人的身影」收成「那人的身影」：
  //    前者是她在宣告自己看見了，後者只是把那件事擺出來 ——
  //    鏡月不邀功，她說的是星說了什麼（同 block 26 的「萬物循理」）。
  //
  //    順帶一提，這也讓最長的標題變短了：原本九個字是四塊面板裡最寬的，
  //    &__panel-title 的 28px 上限就是為了它量的（375px 的機器上佔 91%）。
  //    現在最長的是 block 20 的「原來是這樣轉動的」八個字。
  {
    type: "panel",
    //head "sm"：上一塊的對話框只掛出去 4.5%，不需要 block 20 那種大留白
    head: "sm",
    //背景跟 block 11／20／27 同一張星圖花紋 —— 四塊命書面板是同一種東西
    bg: `${IMG}jingyue-story-bg-1.jpg`,
    title: "那人的身影",
    bodyFrom: "aiQing",
    //引擎還沒做，先用佔位內文讓這一塊掛得上來（同 block 11／20／27）。
    //  bodyFrom 拿得到東西時它就讓位，引擎做好之後把這一行刪掉即可。
    body: PANEL_PLACEHOLDER,
  },

  //32. 面板之後她伸出手。第一句是鋪陳（淡出），
  //    後兩句是邀請，最後把「指點迷津」挑出來換成靛紫 ——
  //    作法同 block 12 的「命局」。
  //
  //    後兩句都加 flat：量設計稿，這兩行**完全沒有光暈**
  //    （離字 1-2px 是 rgb(40,40,40)、3-5px 剩 5，而且 B/R = 1.00
  //      —— 中性灰，那只是抗鋸齒）。
  //    靛紫那四個字自己就是重點，白字底下再墊光會跟它們搶。
  //
  //    字級量出來是 28／33／33（字距 57 與 67.5px，圖是 2.045 倍）。
  {
    type: "text",
    //tail "xl"：話說完要停很久，她才把扇子舉起來
    tail: "xl",
    lines: [
      //fade-soft 而不是 fade：量到這一行最亮處是 240，
      //  接近 fade-soft 的起點（242）；fade 是從 206 起降，會太暗。
      { text: "其實你心中已有浮現出來", style: "strong fade-soft md" },
      {
        text: "把手給我…",
        style: "strong flat xl-soft",
        gap: "lg",
      },
      {
        parts: ["我會為你", { text: "指點迷津" }],
        style: "strong flat xl-soft",
      },
    ],
  },

  //33. 她雙手合十，星芒在掌間亮起。框掛在圖片下緣之外一大截 ——
  //    橫式的圖只佔上面一條，框往下壓在黑底上，像話還沒說完。
  {
    type: "image",
    src: `${IMG}jingyue-story-12.jpg`,
    bubbles: [
      {
        text: "指尖星芒\n早已刻下了\n你的命途",
        //  擬合出來的（殘差 4.2px，圖寬 900）——
        //  框的上半截疊在人像上抓不到邊，只取落在純黑上的那幾列解橢圓。
        top: "70.8%",
        left: "10.3%",
        width: "43.0%",
        //量設計稿：行距 84px ⇒ 字級 56px，框寬 387px ⇒ 0.145
        ratio: 0.145,
        aspect: "0.765",
        //這顆比預設淡（0.63 vs 0.7）—— 底下是夜色不是亮處，
        //  淡一點讓星圖透上來。量設計稿：框內在黑底上是 rgb(158,158,158)。
        fill: 0.63,
      },
    ],
    //框的下緣落在圖高的 152.9%，掛出去 52.9% 的圖高。
    //  這張圖是橫式（圖高是圖寬的 0.6854 倍），
    //  換算成圖寬是 52.9% × 0.6854 ≈ 36.2%。
    spill: "36.2%",
  },

  //34. 接著把話收在「非凡宿命」四個字上。
  //    兩句都 flat：理由同 block 12／32 —— 那四個字自己就是落點，
  //    白字底下再墊光會跟它們搶。
  //
  //    這四個字用**漸層**不是平塗（part.gradient）：
  //    整頁到這裡是最後一句話，份量要比前面那些平塗的重點字再重一級。
  //
  //    ⚠️ 你的參考圖上那道漸層還是白夜的青綠（量到收在 rgb(93,151,172)）。
  //    這裡用的是鏡月自己的白→靛紫（scss 的 $jingyue-text-gradient），
  //    跟 block 2、block 17「十二宮位」是同一道。
  //
  //    字級量出來是 33px（字距 67.5px、圖是 2.045 倍），比原本的 md 大一級。
  {
    type: "text",
    //head：上一塊那顆框掛出圖外很多，這句要等它落定再出現
    head: true,
    //tail "lg"：這句收完才進字卡，中間要明確斷開
    tail: "lg",
    lines: [
      { text: "只屬於", style: "strong xl-soft flat" },
      {
        parts: ["你的", { text: "非凡宿命", gradient: true }, "…"],
        style: "strong xl-soft flat",
      },
    ],
  },

  //35. 字卡式的旁白，直接壓在畫面上（不是對話框）——
  //    這一句不是她對你說的話，是這一頁在替她總結，
  //    所以不給氣泡，像電影的字卡橫在畫面中間。
  //
  //    量設計稿：字距 62.5px（圖是 2.045 倍）⇒ 字 30px，也就是 __line 的
  //    預設值，所以不另外給字級。
  //
  //    ⚠️ top 是估的。這張圖上下各有 10% 的 mask 淡出，所以在截圖上
  //    量不到元素真正的邊 —— 44% 是從「看得見的那一段」反推的，
  //    誤差大約正負 1.5%。擺上去覺得偏高或偏低就直接調這個數字。
  //
  //    用 glow 不用 strong：這一句壓在畫面上，底下是有亮度的圖 ——
  //    光是為了把字從圖裡拉出來，不只是語氣。
  //    （這一塊的光暈沒辦法量：底不是純黑，量到的是圖本身的顏色。）
  {
    type: "image",
    src: `${IMG}jingyue-story-13.jpg`,
    captions: [
      {
        top: "44%",
        lines: [{ text: "你命裡的星正在發光", style: "strong glow" }],
      },
    ],
  },

  //36. 報告的份量。中間那條線比前面兩條都長 ——
  //    停得越久，線就越長（見 scss 的 &--jade）。
  //
  //    字級量設計稿：前兩句 30px（字距 62.5px、圖是 2.045 倍），
  //    線下那句 28px。比白夜那版（28／26／25）整個大一級。
  {
    type: "text",
    //head "lg"：上一塊是滿版的字卡，要明確分開。
    //tail "lg"：下一塊是她的正面特寫。試過 "xl"（130px），下面太空了 ——
    //  上下不必對稱：上面要跟字卡斷開，下面只是換到下一張圖。
    head: "lg",
    tail: "lg",
    //整塊都不發光（flat）—— 前面剛過一張壓了字的圖，
    //  這裡再發光會讓兩塊黏在一起，分不出哪裡結束哪裡開始。
    lines: [
      { text: "描述你的三萬字報告", style: "strong lg flat" },
      //量到這一句是 rgb(143,150,255)，就是整頁那個靛紫。
      //
      //  ⚠️ 用 fit 不用 xl-soft：十個字 × 33px = 330px，
      //  而 375px 的視窗可用寬只有 327px，剛好差 3px 折行
      //  （同 block 24／28）。fit 的上限一樣是 33px，窄螢幕才自己縮。
      //  也不能寫 md —— 那一條在 .is-jade-strong 裡是 0,3,0，壓得過 is-fit。
      {
        text: "一字一句皆由星辰定奪",
        style: "jade-strong flat fit",
        fit: "32px",
        fitSm: "27px",
      },
      { divider: "jade" },
      //  ⚠️ 你的參考圖寫的是「大運」，這裡維持「大限」——
      //  紫微的十年一步叫大限，大運是八字的說法（見 block 18 的註解）。
      //  那張 mock 應該是改名之前做的。要改回去跟我說。
      { text: "你的命格、你的大限", style: "strong md flat" },
    ],
  },

  //37. 她豎起食指要你噤聲。框在右下，掛出圖外 10.3% 的圖高。
  //
  //    ⚠️ 這張圖比白夜那張更瘦長（1.903 vs 1.792 倍），
  //    所以 spill 不能沿用 —— 同樣掛出去「幾 % 的圖高」，
  //    換算成圖寬會差一截。算式見下面。
  {
    type: "image",
    src: `${IMG}jingyue-story-14.jpg`,
    bubbles: [
      {
        text: "全藏在\n漫天星宿裡",
        //擬合殘差 0.22px（圖寬 900）—— 這顆框整個下半截都落在純黑上，
        //  是這一頁所有框裡最乾淨的一次量測。
        top: "74.7%",
        left: "39.9%",
        width: "50.5%",
        //量設計稿：字距 66.5px、框寬 455px ⇒ 0.146
        ratio: 0.146,
        aspect: "0.747",
        //靛紫光，不是白夜那顆的綠。量設計稿（框內填色 180 為基準）：
        //  離字 1-2px (−23.4, −22.5, +9.0)、3-5px (−5.1, −4.4, +21.1)
        //  R 與 G 一起掉、B 反而升 —— 同 block 5／14／19／30 那幾顆。
        glow: true,
      },
    ],
    //框的下緣落在圖高的 110.3%。
    //  算式：框底 = top + 框高 = 74.7% + (50.5% ÷ 0.747 ÷ 1.9027) = 110.3%
    //  （1.9027 是這張圖的高寬比 1975/1038）
    //  要讓出的高度 = 10.3% 的圖高 × 1.9027 ≈ 19.5%。
    spill: "19.5%",
  },

  //38. 收束。前兩句平述，最後四個字用靛紫壓下來（量到 rgb(140,146,255)）。
  //
  //    tail "lg"：「顯露無遺」講完到第一章之間要空一大段。
  //    這裡是整頁最大的一次轉折 —— 前面都是她在跟你說話，
  //    下一塊開始是章節目錄（在預告報告裡有什麼）。
  //    兩塊貼著的話，那四個字會被下一塊的火焰背景接走，像同一段。
  //
  //    章節那一塊自己已經有 64px 的上內距，所以實際的縫是 90 + 64。
  //    —— 為什麼拿掉 size: "sm" ——
  //    量設計稿：前兩句的字距 62.5px、「顯露無遺」100px（圖是 2.045 倍）。
  //    後者帶 0.2em 字距（is-wide），所以字級是 100 ÷ 1.2 ÷ 2.045 ≈ 42px
  //    —— 那正是 .is-jade-strong 的**基本值**。
  //    留著 --sm 的話它會被壓到 35px，前兩句也只有 26px。
  {
    type: "text",
    tail: "lg",
    lines: [
      { text: "不必開口", style: "strong flat lg" },
      //  ⚠️ 是「紫微」不是「紫薇」。你的參考圖上寫的是紫薇（那是花），
      //  紫微才是北極星／紫微斗數的那個微。整個產品都叫紫微斗數，
      //  這一句又正好在講命盤，寫錯會很顯眼。要用紫薇跟我說。
      { text: "早就透過紫微命盤", style: "strong flat lg" },
      //gap 用一般的（0.8em）而不是 "lg"：
      //  margin 是以自己的字級算的，而這一行是 42px、比上面兩句大 ——
      //  同樣寫 "lg" 在這裡會變成 67px，讀起來像換了一段。
      //  這四個字是接著「早就透過紫微命盤」講完的，停一口氣就夠。
      { text: "顯露無遺", style: "jade-strong wide", gap: true },
    ],
  },

  //39. 章節目錄卡。這是整頁第一次「預告後面有什麼」——
  //    前面都在講他看到了什麼，這裡開始賣報告的內容。
  //
  //    跟命書面板的差別是不鋪黑底：那一塊要擋住背景好讀長文，
  //    這一塊要讓火焰透上來，字直接壓在畫面上。
  {
    type: "chapter",
    bg: `${IMG}jingyue-story-bg-3.jpg`,
    //不要在文案裡打空白：膠囊已經有 letter-spacing，
    //  兩份空隙疊起來會變成「第　一　章」那種過寬的排法
    badge: "第一卷",
    title: "剝離星盤的虛偽面具",
    //  ⚠️ 這三條是在賣報告的內容，所以標籤只能承諾**每一張盤都生得出來**
    //  的東西。實測 4416 張盤之後改了兩個標籤，見各條下方的註解。
    items: [
      {
        //  原本是「紫微天機總覽」。天機在紫微裡**也是一顆主星的名字**，
        //  懂行的人會讀成「紫微星與天機星的總覽」。改成星盤就沒有歧義。
        label: "紫微星盤總覽",
        text: "鏡月心眼撥動，十四主星為你寫下的秘密劇本",
      },
      {
        //  身宮、五行局（木三局那些）排盤器都回得出來，而且人人都有。
        label: "身宮與局象暗湧",
        text: "五局定數，決定你一生氣場與命格底色的骨骼能量",
      },
      {
        //  原本是「命宮主星煞氣」。兩個數字都是跑引擎量的（4416 張盤）：
        //    命宮是空宮          15.2%  ← 沒有主星可講
        //    命宮沒有四煞        69.8%  ← 七成的人煞星根本不在命宮
        //  所以標籤不能寫死「命宮主星」也不能寫死「命宮煞星」。
        //  改成「格局」（空宮本身就是一種格局）與「三方煞星」
        //  （紫微本來就是看三方四正的煞星，不是只看本宮）。
        label: "命宮格局與三方煞星",
        text: "你骨子裡壓不住的狂傲、煞星本性與破局天賦",
      },
    ],
  },

  //40. 第二卷。
  {
    type: "chapter",
    bg: `${IMG}jingyue-story-bg-3.jpg`,
    badge: "第二卷",
    title: "心眼所及的暗夜深淵",
    items: [
      {
        label: "戴著面具的人間玩家",
        //  ⚠️ 對外那張臉是**遷移宮**，不是命宮 —— 命宮是本我。
        //  原本寫「命宮外在偽裝」把兩宮顛倒了。
        //  遷移宮三方四正有主星 100%，這一條每張盤都成立。
        text: "遷移宮在外的樣子 vs. 福德宮深處的厭世真相",
      },
      {
        label: "靈魂運轉的詭譎軌跡",
        //  改用命宮：福德宮上一節已經用過了。
        //  整卷三節要落在三個不同的宮（遷移／福德＋命宮／大限），
        //  不然一卷只用到兩個宮，三節會寫成同一段話的三種說法。
        text: "你不可告人的思維邏輯與命宮藏著的陰暗面",
      },
      {
        //  可行性 ✓：實測 4416 張盤，**每一張**的四煞都落在 3 到 4 個宮位
        //  （沒有四煞的盤是 0%）。每個宮位都有自己的大限，
        //  所以「大限逢煞」一定找得到至少一段，人人都講得出來。
        label: "當絕境降臨",
        text: "大限逢煞逼到極限時，你命盤顯露的危險本性",
      },
    ],
  },

  //41. 第三卷。
  {
    type: "chapter",
    bg: `${IMG}jingyue-story-bg-3.jpg`,
    badge: "第三卷",
    title: "人世紅塵的星芒博弈",
    //  ⚠️ 宮名一律用「朋友宮」，不要寫「奴僕宮」。
    //  兩個是同一宮（奴僕是傳統名、朋友是現代名），但排盤器回的、
    //  以及 block 13 那張命盤上印的都是朋友宮 ——
    //  同一頁上盤寫朋友、目錄寫奴僕，使用者會以為是兩回事。
    //  （2026-10-14 Kevin 決定統一成朋友宮。）
    //
    //  三條各佔一個面向，不重疊：
    //    遷移宮（在人前的樣子）→ 命格體質（吸引什麼人）→ 朋友宮（會被誰背叛）
    //  第一條本來也寫朋友宮，拿掉了 —— 那一條講的是「你怎麼對外」，
    //  遷移宮就講得完；朋友宮留給第三條，背叛本來就是它最典型的斷法。
    items: [
      {
        label: "人際戴面具指南",
        text: "遷移宮星曜交錯下，你的社交防禦與捕食本能",
      },
      {
        label: "孽緣還是良緣",
        text: "你這命格體質，究竟在瘋狂吸引哪些危險靈魂",
      },
      {
        label: "人際籌碼與致命盲點",
        text: "你以為的交情，其實隱藏著被朋友宮背叛的風險",
      },
    ],
  },

  //42. 第四卷。
  {
    type: "chapter",
    bg: `${IMG}jingyue-story-bg-3.jpg`,
    badge: "第四卷",
    //  ⚠️ 標題不要指名格局。原本是「官祿殺破狼的事業指南」，
    //  但殺破狼只有 26.1% 的人事業宮有 —— 放在**標題**等於對全部的人承諾。
    //  改用「明暗」：那是廟旺陷，每顆主星都有、排盤器每張盤都回得出來
    //  （getBoard 的 主星[].廟旺），而且「星的明暗」正好是鏡月的語彙。
    title: "星曜明暗的事業指南",
    //  ⚠️ 宮名用「事業宮」不是「官祿宮」—— 同 block 41 那條規則，
    //  排盤器與 block 13 那張盤印的都是事業宮。
    items: [
      {
        //  ⚠️ 原文是「紫微化權還是殺破狼？」—— 那是個**假的二選一**。
        //  實測 4416 張盤：
        //      紫微化權落在事業宮    0.9%   ← 一百一十個人才一個
        //      事業宮有殺／破／狼    26.1%
        //      兩者都沒有            73.4%  ← 七成三的人這一句落空
        //  （紫微化權本身只有壬年生的人有，約 10.8%，還得剛好落在事業宮。）
        //  所以改成不指定星曜，只講「事業宮坐著什麼」——
        //  那是每張盤都答得出來的。
        label: "事業宮與野心",
        text: "事業宮坐著哪顆星，你注定爬上的高位與局外人抉擇",
      },
      {
        label: "修羅場還是避風港",
        text: "最能誘發你事業宮潛能（或把你逼瘋）的工作環境",
      },
      {
        //  ⚠️ 原本寫「哪顆化忌或陀羅星」，旁邊還註了「可行性 ✓，每張盤
        //  一定有一顆化忌」—— 那句話本身沒錯，但**錯在範圍**：
        //  化忌一定在盤上某一宮，不代表它落在事業線上。
        //  重量 13248 張盤，看的是事業宮的三方四正：
        //      有陀羅          33.8%
        //      有化忌          30.8%
        //      兩者至少一個    54.3%   ← 近一半的人一顆都沒有
        //      放寬成煞曜或化忌 93.3%
        //  所以不點名星。這跟第五卷「武曲太陰誰來坐鎮」是同一個模式：
        //  只要寫成「某某星在某宮」，命中率就掉到五成上下。
        label: "職涯必渡的劫數",
        text: "哪一顆煞曜或化忌，會把你絆得血流成河",
      },
    ],
  },

  //43. 第五卷。
  {
    type: "chapter",
    bg: `${IMG}jingyue-story-bg-3.jpg`,
    badge: "第五卷",
    //  標題沒問題：武曲、太陰、天府都是十四正曜、祿存必在盤上，
    //  所以「財星」人人都有 —— 它沒有承諾那些星在財帛宮。
    title: "聚散無常的財星獵場",
    items: [
      {
        //  ⚠️ 原文是「武曲太陰誰來坐鎮？」—— 又是假的二選一。
        //  實測 4416 張盤：武曲或太陰坐財帛／田宅只有 30.8%，
        //  近七成的人這一句落空。改成不指名星曜（同第四卷的作法）。
        label: "財帛與田宅獵場",
        text: "哪顆星來坐鎮？你該靠什麼門路讓錢自動送上門",
      },
      {
        //  ⚠️ 原文是「地空地劫暗中吞噬」。實測：
        //      地空／地劫**在財帛宮**          23.6%
        //      放寬到財帛的三方四正            64.7%
        //      連四煞一起算（煞曜）、三方四正  92.1%
        //  連最寬的算法都有 8% 的人落空，所以乾脆不指名 ——
        //  「錢從哪裡漏掉」每張盤都答得出來（沒有煞曜本身也是一種答案）。
        label: "財帛宮破洞",
        text: "錢從哪裡悄悄漏掉，讓你不知不覺散盡家財的盲點",
      },
      {
        //  可行性 ✓：沒有指名任何星曜或格局。
        label: "聚寶盆開箱",
        text: "最適合你的吸金命格與擺脫貧窮翻身術",
      },
    ],
  },

  //44. 第六卷。
  {
    type: "chapter",
    bg: `${IMG}jingyue-story-bg-3.jpg`,
    badge: "第六卷",
    title: "夫妻宮裡的執念與宿劫",
    items: [
      {
        label: "渴望被愛又害怕被看穿",
        text: "你深夜裡最真實的病態戀愛觀",
      },
      //吃流年層（見第九卷的註）。不寫「桃辰」—— 那不是紫微的術語，
      //  紫微的桃花星是紅鸞、天喜、咸池、天姚
      {
        label: "命中注定的情劫",
        text: "紅鸞星動的哪一年他渡海而來，哪一年與你擦肩",
      },
      //對象的樣子看的是「你的夫妻宮」，不是對方的命盤 —— 我們永遠只有使用者的生辰
      { label: "致命吸引力", text: "你為什麼總是淪陷在煞曜指向的那種人手裡" },
    ],
  },

  //45. 第七卷。
  {
    type: "chapter",
    bg: `${IMG}jingyue-story-bg-3.jpg`,
    badge: "第七卷",
    title: "疾厄宮藏匿的肉身密碼",
    items: [
      {
        label: "疾厄宮肉身警報",
        text: "紫微十二宮裡，老天在你這具軀殼埋下的先天弱點",
      },
      {
        label: "被忽略的器官警訊",
        text: "你以為是小毛病，實則是疾厄宮星曜發出的求救訊號",
      },
      //不寫「調和五行」—— 那是八字的喜用神，紫微的五行局只管起運歲數與
      //  紫微星落點，算不出誰缺什麼。順著星曜本身的屬性給方向才是我們做得到的
      {
        label: "養元退煞法門",
        text: "順著疾厄宮星曜的屬性，哪一套調養方向真的對你有用",
      },
    ],
  },

  //46. 第八卷。
  {
    type: "chapter",
    bg: `${IMG}jingyue-story-bg-3.jpg`,
    badge: "第八卷",
    title: "父母田宅的宿世因果",
    items: [
      //不帶兄弟宮 ——「兄弟宮主母」是派別讀法，主流的兄弟宮就是手足，
      //  而且它不在父母宮的三方四正（父母、疾厄、朋友、子女）裡
      {
        label: "父母宮裡的恩怨",
        text: "父母是你的貴人還是債主，這一世來還還是來討",
      },
      {
        label: "田宅宮深處的枷鎖",
        text: "原生家庭與家業陰影，在你靈魂烙下的痛",
      },
      {
        label: "血脈裡的宿命清算",
        text: "如何從家庭內耗中解脫，拿回屬於你的命宮主導權",
      },
    ],
  },

  //47. 第九章，最後一章。
  {
    type: "chapter",
    bg: `${IMG}jingyue-story-bg-3.jpg`,
    badge: "第九卷",
    //tail：九章列完了，下一塊是他的收口，中間要斷開
    tail: true,
    title: "十年大限的逆天翻盤",
    //這一卷的②③ 要「哪一年」，吃的是流年層（大限只到十年）。
    //  決定接流年：calculator 的 buildFlowPlatePalaces 本來就寫好了，
    //  正式報告引擎開工時一起接。只有這三節與第六卷「情緣的來與去」吃流年，
    //  其餘 24 節維持大限 —— 27 節全塞流年 prompt 會肥一大圈，而且疾厄、
    //  父母、田宅那些本來就不需要年份。
    items: [
      {
        label: "大限伏擊期",
        text: "那些看似沉寂、實則在暗中蓄力的運勢醞釀期",
      },
      {
        label: "十年大限劇變",
        text: "四化引動的那一年，你的命運將會如何被徹底翻轉？",
      },
      {
        label: "逆天改命的節奏",
        text: "踩準紫微流年與大限轉折點，徹底改寫你的宿命劇本",
      },
    ],
  },

  //48. 九章列完之後的收口：他問你準備好了沒。
  {
    type: "image",
    src: `${IMG}jingyue-story-15.jpg`,
    bubbles: [
      {
        text: "準備好\n看透你半生的\n星軌浮沉了\n嗎？",
        //  左緣與框寬量出來跟白夜那版一模一樣（40.39% / 48.44%）——
        //  是同一顆橢圓換了位置，所以 aspect 沿用 0.735，只有 top 重量。
        //  橢圓用列掃描擬合：a = 218、b = 296.6、中心 (650.5, 1556)，
        //  殘差最大的是最寬那幾列（接近頂點，半寬對 y 不敏感），已剔除。
        top: "68.6%",
        left: "40.4%",
        width: "48.4%",
        ratio: 0.125,
        aspect: "0.735",
        //量設計稿：距字緣 3-4px Δ=(−10.2, −8.9)，R 與 G 一起掉、B 往上
        //  → 靛藍，不是白夜那顆綠的。跟其他五顆鏡月的框同一組值。
        glow: true,
      },
    ],
    //下緣落在圖高的 105.7% → 溢出 5.7% 的圖高。
    //  這張圖比例 0.5619（圖高是圖寬的 1.78 倍），換算成圖寬是 10.1%。
    spill: "10.1%",
  },

  //49. 進入用戶回饋段的標頭。第二行用漸層字 ——
  //    量到由上而下 255 → 168，而且藍綠通道逐漸高於紅。
  {
    type: "text",
    //head "lg"：上一塊那顆框掛出圖外 10.1%，這段標頭要等它落定再出現
    head: "lg",
    lines: [
      { text: "閻異閣-鏡月仙子", style: "strong md" },
      { text: "真實用戶回饋", style: "gradient xl-soft bold" },
    ],
  },

  //50. 用戶回饋。信箱是遮罩過的，評語裡的換行照使用者原本打的斷。
  //
  //  這幾則提到的東西都對得上報告做得到的範圍：大限解析、紫微化忌、
  //  十四主星都是現成的；「前年換工作的時間點」「哪幾年走運」「今年化忌」
  //  吃的是流年層 —— 那是已經決定要接的（見第九卷的註）。
  //  最後一則的「這個月」是使用者自己的體感，不是報告給的流月預測 ——
  //  排盤器沒有流月，不要往那個方向加文案。
  {
    type: "reviews",
    items: [
      {
        email: "e2****@gmail.com",
        stars: 5,
        text: "看完鏡月仙子的解析真的雞皮疙瘩… 😱\n它直接講出我這幾年在職場被壓抑的感覺\n還告訴我不是我不夠好 而是我放錯位置\n改了方向後真的順很多！",
      },
      {
        email: "cl*****@gmail.com",
        stars: 5,
        text: "原本以為只是普通的紫微測算，結果大限解析那段\n精準講中我前年換工作跟失戀的時間點\n內容超級豐富，完全不是那種套公式的罐頭文字，\n強烈推薦！",
      },
      {
        email: "ty******@gmail.com",
        stars: 5,
        text: "講中我今年紫微化忌的破財危機🤣🤣🤣\n看完報告立刻調整投資策略 直接幫我避開一個大坑 這錢根本花得太划算了！",
      },
      {
        email: "ky*********@gmail.com",
        stars: 5,
        text: "第一次看到把紫微十四主星拆解得這麼白的報告\n把我的性格優勢跟致命傷寫得超透徹\n看完終於不再盲目焦慮了 很有方向感",
      },
      {
        email: "we****@gmail.com",
        stars: 5,
        text: "準到有點毛骨悚然… 😅 把我和另一半相處時最常卡住的矛盾點全部寫出來，連我私下藏很深的感情執念都講透了！",
      },
      {
        email: "b0******@gmail.com",
        stars: 5,
        text: "本來抱著試試看的心態 結果看完直接掉眼淚\n報告裡不只準 還給了很具體的破局建議!!! 感覺整個人被理解了",
      },
      {
        email: "vj****@gmail.com",
        stars: 5,
        text: "看過那麼多紫微算命，這家真的最誇張！\n連我哪幾年走運、哪幾年會遇到小人都寫得清清楚楚@@",
      },
      {
        email: "er*******@gmail.com",
        stars: 5,
        text: "太神了啦！\n直接點我為什麼一直被小人絆住💢\n照著報告裡的建議調整之後 這個月職場運勢真的明顯變順！",
      },
    ],
  },
];

//對話框裡的字佔框寬的幾成（預設值）。
//  框放大之後字若照同一個比例跟著長，會把橢圓塞滿、少了留白 ——
//  0.29 讓「嗯…」在框裡浮著，四周留得開。
//
//  字多的框要自己覆寫（bubble.ratio）：同樣是一顆橢圓，
//  放兩個字和放四行三個字，能用的字級差一倍。
const BUBBLE_TEXT_RATIO = 0.29;

//把「框寬佔圖的百分比」換算成字級。
//  兩者都以整張圖為基準（container-type 在 .jingyue-story__frame 上），
//  所以 15% 的框配 15 × 0.29 ≈ 4.4cqw 的字。
//  框改大小時字自動跟著，不必兩個數字一起調。
const bubbleFontSize = (width, ratio = BUBBLE_TEXT_RATIO) =>
  `${parseFloat(width) * ratio}cqw`;

//畫一行字。文字 block 與疊在圖上的字幕共用 ——
//  兩邊的一行都支援同樣的東西（style、gap、parts、from、divider、
//  ornament、title），抽出來才不會改了一邊忘了另一邊。
const renderLine = (line, i, data) => {
  //分隔線不是文字，不套 __line 那套字級與顏色 ——
  //  放在 lines 裡是因為它的位置就是「第幾行之後」，
  //  另外開一個欄位的話，改順序時兩邊都要記得動。
  //章節大標：三層同一組字疊在一起（左右暗分身 + 中間亮的）。
  //  分身用 aria-hidden：讀屏只該唸到一次「大限」。
  if (line.title)
    return (
      <span key={`title-${i}`} className="jingyue-story__title">
        {["l", "r"].map((side) => (
          <span
            key={side}
            className={`jingyue-story__title-layer jingyue-story__title-layer--${side}`}
            aria-hidden="true"
          >
            {line.title}
          </span>
        ))}
        <span className="jingyue-story__title-layer jingyue-story__title-layer--core">
          {line.title}
        </span>
      </span>
    );

  //飾紋：一條橫的裝飾圖。跟 divider 一樣是「不是字，但位置是第幾行」
  if (line.ornament)
    return (
      <img
        key={`ornament-${i}`}
        className="jingyue-story__ornament"
        src={line.ornament}
        alt=""
        loading="lazy"
      />
    );

  //divider: true 是預設（靛紫），給字串就是變體（目前只有 "plain"）
  if (line.divider)
    return (
      <span
        key={`divider-${i}`}
        className={[
          "jingyue-story__divider",
          line.divider !== true && `jingyue-story__divider--${line.divider}`,
        ]
          .filter(Boolean)
          .join(" ")}
      />
    );

  //每一行可以只給字串（一般白字），或給物件帶樣式。
  //  兩種寫法並存是刻意的 —— 大多數行都是白字，
  //  全部都寫成 { text: "…" } 只是讓表變難讀。
  const { text, style, gap, from, fit, fitSm } =
    typeof line === "string" ? { text: line } : line;

  //—— fit：不折行的字級 ——
  //
  //  給「字多、而且不能折行」的句子用。字級變成
  //  min(上限, 可用寬 ÷ 字數)：寬螢幕就是上限，窄螢幕才開始縮。
  //
  //  字數是**數出來的**，不是寫在表裡 —— 第一版把除數寫死在 scss，
  //  結果是「改了文案就要回去改那個數字」，而沒有人會記得。
  //  [...text] 而不是 text.length：用展開運算子才會按「字」數，
  //  碰到表情符號或罕用字（代理對）才不會數成兩個。
  const fitVars = fit
    ? {
        "--chars": [...String(text || "")].length,
        "--fit-max": fit,
        ...(fitSm ? { "--fit-max-sm": fitSm } : {}),
      }
    : undefined;

  //from 的行內容要等資料（暱稱、四柱），作法同對話框的 bubble.from。
  //  BLOCKS 是模組層的常數，元件拿到 intake 之前就建好了，
  //  所以表裡只能記「要哪一個欄位」，值在這裡才查得到。
  //parts：一行裡有局部換色的重點字（見 block 12）。
  //  拆成陣列而不是在字串裡塞標記（例如 **命格**）——
  //  那需要一個剖析器，而整份資料表只有這一種強調，不值得。
  const content = line.parts
    ? line.parts.map((part, j) =>
        typeof part === "string" ? (
          part
        ) : (
          <span
            //gradient: true 讓這兩個字用漸層而不是平塗的靛紫 ——
            //  平塗是「被唸重了」，漸層是「整段的落點」，份量再重一級。
            className={[
              "jingyue-story__mark",
              part.gradient && "jingyue-story__mark--gradient",
            ]
              .filter(Boolean)
              .join(" ")}
            key={`${j}-${part.text}`}
          >
            {part.text}
          </span>
        ),
      )
    : from
      ? data?.[from]
      : text;

  return (
    <p
      key={from ? `${from}-${i}` : `${text || "parts"}-${i}`}
      //給底部按鈕當觸發點用（見 CTA_TRIGGER_ID）。
      //  用 id 而不是 ref：renderLine 是一般函式不是元件，
      //  要掛 ref 得把整條渲染路徑改成元件才行。
      id={line.id}
      style={fitVars}
      className={[
        "jingyue-story__line",
        //style 可以給多個，用空白分隔（例如 "strong lg"）——
        //  一行的「顏色與字重」和「大小」是兩件事，
        //  每一種組合各開一個 class 的話會變成排列組合。
        style &&
          style
            .split(" ")
            .map((n) => `is-${n}`)
            .join(" "),
        //和上一行之間空一段。gap: true 是一整段的停頓，
        //  gap: "sm" 是同一口氣裡鬆一點的換行。
        gap && (gap === true ? "has-gap" : `has-gap-${gap}`),
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {content}
    </p>
  );
};

//畫一個 block。
//  版型判斷收在這裡，不寫進 BLOCKS 那張表 ——
//  表只描述「有什麼內容」，這裡才決定「長什麼樣」。
//
//  data 是「要等 API 才有的內容」（目前只有四柱）。
//  刻意用具名欄位查表（bubble.from）而不是把值直接塞進 BLOCKS：
//  BLOCKS 是模組層的常數，在元件還沒拿到 intake 之前就建好了。
//—— 命盤 ——
//
//  整頁唯一有狀態的區塊：點（或滑過）一宮會浮出那一宮的副星。
//
//  —— 副星為什麼不直接畫在格子裡 ——
//  面板裡是**雜曜**（長生十二神、歲前、將前、博士與其餘雜曜）。
//  甲級副星不在這裡 —— 它們跟主星一起畫在格子上（會帶四化，
//  收進來的話那些化曜就從盤面上消失了）。
//
//  雜曜最多的一宮有十二顆（實測子女宮），一格一百 px 根本畫不下；
//  而且它也不是讀命盤的第一層資訊 —— 先看主星落在哪一宮，
//  要細看時才去翻這些。所以收進面板：格子乾淨了，一顆也沒有少。
const BoardBlock = ({ block, data }) => {
  //  同時只開一宮。存地支而不是索引 —— 地支是這張盤的唯一識別，
  //  索引會隨版面的排法改變。
  const [openZhi, setOpenZhi] = useState(null);

  const board = data?.[block.from];
  //排盤還沒回來（或失敗）就整塊不畫 —— 空盤比沒有盤更像壞掉
  if (!board?.宮格?.length) return null;

  //  地支 → 那一宮。版面是依地支填格子的，不是依十二宮的順序 ——
  //  命宮落在哪一格因人而異，但「申在右上角」是固定的。
  const byZhi = {};
  board.宮格.forEach((g) => {
    byZhi[g.支] = g;
  });

  //  命宮在 BOARD_ZHI 裡的位置，決定中宮那組三方四正怎麼連。
  //  找不到就不畫線（不是錯誤：線是裝飾，盤才是內容）。
  const lines = BOARD_LINES[BOARD_ZHI.indexOf(board.命宮)] || [];

  //  滑鼠才跟著游標開合。觸控螢幕上 pointerenter 會跟著 tap 一起發，
  //  不擋的話手機上會變成「點一下開、放開又被 enter 開一次」。
  const hoverOnly = (fn) => (e) => {
    if (e.pointerType === "mouse") fn();
  };

  //  一格的內容。星名直書 —— 那是命盤的讀法，
  //  橫著寫的話「紫微貪狼」會變成一個四字詞，看不出是兩顆星。
  const cell = (zhi) => {
    const g = byZhi[zhi];
    if (!g) return null;
    const [row] = BOARD_POS[zhi];
    const open = openZhi === zhi;

    return (
      <>
        <button
          type="button"
          className={[
            "jingyue-story__board-cell",
            g.是命宮 && "is-life",
            open && "is-open",
          ]
            .filter(Boolean)
            .join(" ")}
          //  aria-expanded 要有：這顆鈕唯一的作用就是展開那塊面板，
          //  讀屏沒有它只會唸出一串星名，不知道還能按
          aria-expanded={open}
          onClick={() => setOpenZhi(open ? null : zhi)}
          onPointerEnter={hoverOnly(() => setOpenZhi(zhi))}
          onPointerLeave={hoverOnly(() => setOpenZhi(null))}
        >
          {/* 星群。兩簇分站兩邊：甲級副星靠左上，主星靠右上。
              中間空出來的那一段是刻意的 —— 一宮零到三顆主星、
              零到三顆甲級，兩邊都會伸縮，留空才不會互相擠。 */}
          <div className="jingyue-story__board-stars">
            {/* 甲級副星。小一級、暗一級，但仍在第一層 ——
                它們會帶四化（文昌化科、左輔化科…），份量跟雜曜不同級，
                收進面板的話那些化曜就從盤面上消失了。
                一宮最多三顆（實測財帛宮）。 */}
            <span className="jingyue-story__board-side">
              {g.甲級.map((s) => (
                <span
                  className="jingyue-story__board-star is-minor"
                  key={`a-${s.名}`}
                >
                  {[...s.名].map((ch, i) => (
                    <b key={`${s.名}-${i}`}>{ch}</b>
                  ))}
                  {s.化 && (
                    <em
                      className={`is-hua-${HUA_KEY[s.化]}`}
                      aria-label={`${s.名}化${s.化}`}
                    >
                      {s.化}
                    </em>
                  )}
                </span>
              ))}
            </span>

            {/* 主星。row-reverse：由右往左排，第一顆落在最右邊 ——
                那是命盤的讀法，不是排版喜好。 */}
            <span className="jingyue-story__board-side is-major">
              {g.主星.map((s) => (
                <span className="jingyue-story__board-star" key={s.名}>
                  {[...s.名].map((ch, i) => (
                    <b key={`${s.名}-${i}`}>{ch}</b>
                  ))}
                  {s.廟旺 && <i>{s.廟旺}</i>}
                  {s.化 && (
                    <em
                      className={`is-hua-${HUA_KEY[s.化]}`}
                      aria-label={`${s.名}化${s.化}`}
                    >
                      {s.化}
                    </em>
                  )}
                </span>
              ))}
            </span>
          </div>

          {/* 底列，照傳統命盤的三個位置：
              大限左下、宮職正中、干支直立在右下。
              三者都貼著格子的下緣（align-items: flex-end），
              干支佔兩行也不會把另外兩個頂上去。 */}
          <div className="jingyue-story__board-foot">
            <span className="jingyue-story__board-decade">{g.大限}</span>
            <span className="jingyue-story__board-name">
              {g.宮位}
              {/* 身宮一定跟某一宮同宮，不是第十三個宮 ——
                  所以是掛在宮名旁的記號，不是一個獨立的格子。 */}
              {g.是身宮 && <i>身</i>}
            </span>
            {/* 干支直書。一個字一個 <b> 而不是 writing-mode ——
                理由同星名（見上面）：兩個字要各自控制行距。 */}
            <span className="jingyue-story__board-gz">
              <b>{g.干}</b>
              <b>{g.支}</b>
            </span>
          </div>
        </button>

        {/* 副星面板。蓋在**這一格自己身上**、同寬，像那一格被翻過來。
            所以不需要左右定位的邏輯：width 就是 100%（見 scss）。
            只有上下要分 —— 第四列往上長，不然會掉出盤外。 */}
        {open && (
          <div
            className={`jingyue-story__board-pop is-row-${row}`}
            role="status"
          >
            <p className="jingyue-story__board-pop-body">
              <b>副星</b>
              {g.雜曜.length ? (
                //  用「・」串起來而不是一顆一個方塊：三顆以上時方塊會排不下，
                //  而串起來的那一行可以自己折，折了也還是同一組。
                g.雜曜.map((s, i) => (
                  //  「・」要放在 span **外面**。
                  //  中文預設哪裡都能斷，所以星名要自己 nowrap 保住；
                  //  但全部都 nowrap 而中間又沒有可斷處的話，整串就不會折、
                  //  直接溢出面板。把分隔點留在外面，斷行就只發生在那裡。
                  //  （踩過：寫在 span 裡面，「指背」被折成「指／背」。）
                  <Fragment key={`${s.名}-${i}`}>
                    {i > 0 && <u>・</u>}
                    <span>
                      {s.名}
                      {s.化 && (
                        <i className={`is-hua-${HUA_KEY[s.化]}`}>{s.化}</i>
                      )}
                    </span>
                  </Fragment>
                ))
              ) : (
                <span className="is-none">無</span>
              )}
            </p>
          </div>
        )}
      </>
    );
  };

  //  用 <table> 不用 CSS grid。
  //  這一條是從元辰那支命盤抄過來的經驗：iOS Safari 的 grid 在 1fr
  //  均分時會差個一兩 px，四欄四列加起來就是一條歪掉的框線。
  //  表格的欄寬演算法沒有這個問題，而且中宮要跨兩欄兩列，
  //  rowSpan/colSpan 本來就是表格的事。
  return (
    <section
      className="jingyue-story__board"
      style={{ "--panel-bg": `url(${block.bg})` }}
    >
      <div className="jingyue-story__board-inner">
        {data?.name && (
          <h2 className="jingyue-story__chart-title">
            {data.name} 的命局
            {data.birthDate && (
              <span className="jingyue-story__chart-date">
                {data.birthDate.replace(
                  /^(\d+)\/(\d+)\/(\d+)$/,
                  "$1年 $2月 $3日",
                )}
              </span>
            )}
          </h2>
        )}

        <table className="jingyue-story__board-table">
          <tbody>
            <tr>
              {BOARD_ZHI.slice(0, 4).map((z) => (
                <td key={z}>{cell(z)}</td>
              ))}
            </tr>
            <tr>
              <td>{cell(BOARD_ZHI[4])}</td>
              {/* 中宮。跨兩欄兩列 —— 那是命盤本來就有的一塊空地，
                  三方四正的線畫在這裡，所以不能拆成四格。 */}
              <td colSpan={2} rowSpan={2} className="is-center">
                <div className="jingyue-story__board-center">
                  {/* 線畫在 SVG 上、文字疊在上面。
                      preserveAspectRatio="none"：這塊空地不是正方形
                      （兩欄寬、兩列高），線要跟著拉伸才連得到四個角。 */}
                  {/* 線畫在 SVG 上、文字疊在上面。
                      preserveAspectRatio="none"：這塊空地不是正方形
                      （兩欄寬、兩列高），線要跟著拉伸才連得到四個角。 */}
                  <svg
                    className="jingyue-story__board-lines"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    {lines.map((l, i) => (
                      <line key={i} {...l} />
                    ))}
                  </svg>
                  <div className="jingyue-story__board-meta">
                    <p className="jingyue-story__board-bureau">
                      {board.五行局名}
                    </p>
                    <p>命主 {board.命主}</p>
                    <p>身主 {board.身主}</p>
                  </div>
                </div>
              </td>
              <td>{cell(BOARD_ZHI[5])}</td>
            </tr>
            <tr>
              <td>{cell(BOARD_ZHI[6])}</td>
              <td>{cell(BOARD_ZHI[7])}</td>
            </tr>
            <tr>
              {BOARD_ZHI.slice(8).map((z) => (
                <td key={z}>{cell(z)}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
};

const renderBlock = (block, index, data) => {
  const key = `${block.type}-${index}`;

  if (block.type === "image") {
    //一律包一層 frame：有對話框時它是定位基準，沒有時也統一結構 ——
    //  兩種寫法並存的話，接縫與 first-child 的樣式都要各寫一份。
    //框裡的對話框可以掛出圖片下緣（見 block 9）。溢出來的那一截是「浮」的，
    //  不佔版面高度，所以要自己把那段空間讓出來，不然會壓在下一塊上。
    //
    //  空間讓在這一塊的下緣、而不是叫下一塊多留上緣：
    //  那一截是這張圖的對話框造成的，之後把下一塊換掉或刪掉，
    //  它仍然需要同一段空間 —— 責任留在製造溢出的人身上才不會失聯。
    //
    //  百分比的 margin 是以「包含區塊的寬度」為基準（同 padding 那個坑），
    //  所以 spill 給的是「佔圖寬的百分比」，不是圖高。
    return (
      <div
        key={key}
        className="jingyue-story__frame"
        style={block.spill ? { marginBottom: block.spill } : undefined}
      >
        <img
          className="jingyue-story__image"
          src={block.src}
          alt=""
          //第一張要盡快出現（它就是第一屏），其餘等捲到附近再載
          loading={index === 0 ? "eager" : "lazy"}
        />

        {/* 疊在圖上的字幕：沒有框，整條橫跨、置中。
            位置只給 top —— 旁白的重點是「在畫面的第幾分之幾高」。 */}
        {(block.captions || []).map((caption, c) => (
          <div
            key={`caption-${c}`}
            className="jingyue-story__caption"
            style={{ top: caption.top }}
          >
            {caption.lines.map((line, i) => renderLine(line, i, data))}
          </div>
        ))}

        {/* 一張圖可以有好幾顆對話框（block 14 有兩顆）。
            順序就是 DOM 順序，位置各自由資料給，彼此不影響。 */}
        {(block.bubbles || []).map((bubble, b) => (
          <div
            key={`bubble-${b}`}
            className={[
              "jingyue-story__bubble",
              //光暈是特例不是通則：判語那顆量到完全沒有色偏。
              //  true 是青藍，給字串就是別的顏色（目前只有 "jade" 綠）。
              bubble.glow &&
                (bubble.glow === true
                  ? "jingyue-story__bubble--glow"
                  : `jingyue-story__bubble--glow-${bubble.glow}`),
            ]
              .filter(Boolean)
              .join(" ")}
            //位置與寬度是每張圖各自的，寫在資料裡而不是樣式裡。
            //  字級也設在這一層（不是裡面的 span）—— 框的內距用 em，
            //  而 em 只認元素自己的 font-size（見 scss 裡的 padding）。
            style={{
              top: bubble.top,
              left: bubble.left,
              width: bubble.width,
              fontSize: bubbleFontSize(bubble.width, bubble.ratio),
              //沒給就讓 scss 用預設的 7/10
              ...(bubble.aspect ? { "--bubble-aspect": bubble.aspect } : null),
              //框身的不透明度。沒給就用 scss 的預設（0.7）——
              //  逐框可調是因為量過四張設計稿，每一顆都不一樣：
              //    block  9 四柱      0.63
              //    block 14／19       0.72
              //    block 30 情線如星軌 0.81
              //  底下是什麼決定了要多濃：底圖越亮越花，框就得越實，
              //  黑字才壓得住（見 scss 的 &__bubble）。
              ...(bubble.fill != null
                ? { "--bubble-fill": bubble.fill }
                : null),
            }}
          >
            {/* 固定文案直接給 text，要等 API 的給 from。
                還沒回來時橢圓先空著 —— 這時候放「載入中」反而更吵，
                框本身已經是畫面的一部分，突然冒出來才是閃動。 */}
            <span className="jingyue-story__bubble-text">
              {bubble.from ? data?.[bubble.from] : bubble.text}
            </span>
          </div>
        ))}
      </div>
    );
  }

  //  命盤是整頁唯一有狀態的區塊（副星面板的開合），所以它是一支元件，
  //  不像其他 block 那樣在 renderBlock 裡直接畫出來。
  //  宣告在這個檔案裡而不是另開資料夾：它是這一頁的一部分，
  //  沒有第二個地方會用到，拆出去只會多一層 import 與一個同名樣式檔。
  if (block.type === "board")
    return <BoardBlock key={key} block={block} data={data} />;

  if (block.type === "reviews") {
    return (
      <section key={key} className="jingyue-story__reviews">
        {block.items.map((review, i) => (
          <article
            className="jingyue-story__review"
            key={`${i}-${review.email}`}
          >
            <p className="jingyue-story__review-email">{review.email}</p>
            {/* 星星用字元重複而不是圖示：它要跟文字一起縮放、一起繼承顏色。
                aria-label 才是給讀屏的，★ 本身標成 aria-hidden，
                不然會被唸成五次「黑星」。 */}
            <p
              className="jingyue-story__review-stars"
              aria-label={`${review.stars} 顆星`}
            >
              <span aria-hidden="true">{"★".repeat(review.stars)}</span>
            </p>
            <p className="jingyue-story__review-text">{review.text}</p>
          </article>
        ))}
      </section>
    );
  }

  if (block.type === "chapter") {
    return (
      <section
        key={key}
        className={[
          "jingyue-story__chapter",
          //跟後面那一塊拉開（章與章之間另有 & + & 管）
          block.tail && "jingyue-story__chapter--tail",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{ "--panel-bg": `url(${block.bg})` }}
      >
        <div className="jingyue-story__chapter-inner">
          {block.badge && (
            <p className="jingyue-story__chapter-badge">{block.badge}</p>
          )}
          {block.title && (
            <h2 className="jingyue-story__chapter-title">{block.title}</h2>
          )}

          {/* 條列包一層黑卡。白夜那版是讓火焰直接透上來、字壓在畫面上，
              鏡月這張背景是滿版的星雲 —— 亮處散在整張圖上而不是集中在中間，
              白字沒有底就會在那些亮點上消失。
              （黑卡只包條列，不包膠囊與標題：那兩個在上半部，
                背景在那一段是暗的，壓上去反而把版面切成兩塊。）*/}
          {!!(block.items || []).length && (
            <div className="jingyue-story__chapter-card">
              {block.items.map((item, i) => (
                <p
                  className="jingyue-story__chapter-item"
                  key={`${i}-${item.label}`}
                >
                  <span
                    className="jingyue-story__chapter-mark"
                    aria-hidden="true"
                  >
                    ★
                  </span>
                  {item.label}：{item.text}
                </p>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }

  if (block.type === "panel") {
    //內容可以是陣列（一段一筆）或一整串換行分隔的字。
    //  容忍兩種是因為 AI 回來的是 string[]，但寫死在表裡的可能是一坨文字。
    //  ?? 而不是三元：bodyFrom 指的是 AI 生成的內容，拿不到時
    //  退回表裡寫死的 body（目前只有 block 11 有，當引擎還沒做好的替身）。
    //  用 || 的話空字串也會退回去 —— 那是「生成失敗」，不是「還沒做」，
    //  兩者該走不同的路（空內文整塊不掛，見下面）。
    const body = (block.bodyFrom ? data?.[block.bodyFrom] : null) ?? block.body;
    const paragraphs = Array.isArray(body)
      ? body
      : String(body || "")
          .split("\n")
          .map((t) => t.trim())
          .filter(Boolean);

    //沒有內文就整塊不掛。
    //  掛一個只有標題、底下一片空白的黑卡會比少掉這一塊更像壞掉 ——
    //  而這一頁是連續往下捲的長圖，中間少四塊看不出來。
    //
    //  這同時涵蓋兩種情況：AI 還在路上、以及 AI 生成失敗。
    //  使用者分不出差別，所以不必分開處理。
    if (!paragraphs.length) return null;

    return (
      <section
        key={key}
        className={[
          "jingyue-story__panel",
          //跟文字 block 同名的旋鈕：上緣多留一截，跟前一塊拉開。
          //  true 是預設，給字串就是分級（目前只有 "sm"）。
          block.head &&
            (block.head === true
              ? "jingyue-story__panel--head"
              : `jingyue-story__panel--head-${block.head}`),
        ]
          .filter(Boolean)
          .join(" ")}
        //背景圖寫在 inline style：哪一塊用哪張圖是內容的事，不是樣式的事。
        //  用 CSS 變數而不是直接設 backgroundImage —— 花紋實際畫在 ::before 上
        //  （那一層才做上下淡出），而偽元素沒辦法用 inline style 指定。
        style={{ "--panel-bg": `url(${block.bg})` }}
      >
        <div className="jingyue-story__panel-card">
          {block.title && (
            <h2 className="jingyue-story__panel-title">{block.title}</h2>
          )}

          {paragraphs.map((text, i) => (
            <p
              className="jingyue-story__panel-text"
              key={`${i}-${text.slice(0, 8)}`}
            >
              {text}
            </p>
          ))}
        </div>
      </section>
    );
  }

  if (block.type === "text") {
    return (
      <div
        key={key}
        className={[
          "jingyue-story__text-block",
          //放大是整段的事，所以掛在 block 上而不是逐行掛 ——
          //  掛在行上的話，同一段裡加一行就得記得也標一次。
          block.size && `jingyue-story__text-block--${block.size}`,
          //講完要停一下的段落：下緣多留一大截，下一塊圖才不會緊接著上來。
          //  tail: true 是預設，給字串就是分級（目前只有 "lg"）——
          //  作法同行距的 gap，級距集中在 scss 裡，資料表只說「要哪一級」。
          block.tail &&
            (block.tail === true
              ? "jingyue-story__text-block--tail"
              : `jingyue-story__text-block--tail-${block.tail}`),
          //重新開口的段落：上緣多留一截，跟前一塊拉開。
          //  true 是預設，給字串就是分級（目前只有 "lg"）。
          block.head &&
            (block.head === true
              ? "jingyue-story__text-block--head"
              : `jingyue-story__text-block--head-${block.head}`),
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {block.lines.map((line, i) => renderLine(line, i, data))}
      </div>
    );
  }

  return null;
};

const JingyueStoryPage = () => {
  //—— 動線打點：進故事頁、讀到底 ——
  //
  //  「讀到底」用捲動判斷，不在 JSX 裡放哨兵元素 ——
  //  這一頁的內容是 BLOCKS 資料表畫出來的，塞一個跟內容無關的收尾節點
  //  會讓那張表之外多出一個要記得維護的東西。
  //
  //  打完就把監聽拿掉：捲動事件在手機上一秒觸發幾十次，而這件事
  //  只需要知道一次。留 120px 的餘裕 —— 行動瀏覽器的網址列收合會讓
  //  底部差個幾十 px，要求剛好捲到底的話很多人永遠觸發不了。
  useEffect(() => {
    track("story_view", "ziwei-jingyue");

    const onScroll = () => {
      const el = document.documentElement;
      if (el.scrollTop + el.clientHeight < el.scrollHeight - 120) return;
      track("story_end", "ziwei-jingyue");
      window.removeEventListener("scroll", onScroll);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navigate = useNavigate();
  //捲動容器。按鈕的觸發判斷要以它為基準 ——
  //  這一頁自己捲（見 scss 的 .jingyue-story），不是整個視窗在捲，
  //  IntersectionObserver 的 root 給錯就永遠不會觸發。
  const scrollRef = useRef(null);
  //付款輪詢的計時器。放 ref 是為了離開這一頁時清掉 ——
  //  留著的話它會在元件卸載之後繼續打 API，而且永遠不會停。
  const pollRef = useRef(null);
  const [showCta, setShowCta] = useState(false);
  const [buying, setBuying] = useState(false);

  const { state, search } = useLocation();

  const intake =
    state?.intake || (wantsSampleIntake(search) ? SAMPLE_INTAKE : null);

  //—— 購買流程 ——
  //
  //  購買 sheet（選方案）→ 發票 sheet（載具／統編）→ 建單 → 跳轉綠界。
  //  中間不擋登入：這是單次消費的商品，使用者剛走完十二題問診，
  //  在這裡擋一道登入牆是會掉單的。身分靠發票填的那個信箱
  //  （見後端 P-4b 與 models/order 的 email 欄位說明）。
  //
  //  方案跟後端拿，不寫死在前端 —— 建單時算的金額必須跟畫面上顯示的
  //  是同一份。兩邊各存一份價格，遲早會出現「畫面寫一個數、實際扣另一個」。
  const [plans, setPlans] = useState([]);
  //選完方案、等著填發票的那一份。null 代表發票 sheet 沒開。
  const [pendingPlan, setPendingPlan] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [payError, setPayError] = useState("");

  useEffect(() => {
    PaymentService.plans("report")
      .then(({ data }) => {
        if (!data?.status) return;
        //後端回的是 { id, name, summary, price, original, discount }，
        //  PurchaseSheet 要的是 { id, title, note, price, original } ——
        //  在這裡轉，不要去改那支共用元件的介面（提問頁也在用它）。
        //
        //  ⚠️ 要先濾掉別支產品的方案。/plans?type=report 回的是**所有**
        //  報告類方案（現在有白夜與鏡月兩支），不濾的話鏡月的購買視窗
        //  會同時列出兩份，使用者可以在這一頁買到另一支產品。
        //  濾的是方案 id，對應 config/product-plans.js 的鍵。
        setPlans(
          (data.data?.plans || [])
            .filter((p) => p.id === "jingyue")
            .map((p) => ({
              id: p.id,
              title: p.name,
              note: p.summary,
              price: p.price,
              original: p.original,
            })),
        );
      })
      .catch(() => {
        //拿不到方案時購買 sheet 會是空的。不另外跳錯誤 ——
        //  使用者還在看故事，這時彈一個「載入失敗」只是打斷他。
        //  真的按下購買才會發現，那時 PurchaseSheet 自己會顯示沒有方案。
      });
  }, []);

  //購買 sheet 按下「繼續」：收起它、打開發票 sheet
  const handlePickPlan = useCallback(({ plan }) => {
    setPayError("");
    setPendingPlan(plan);
    setBuying(false);
  }, []);

  //—— 盯著訂單，等它變成已付款 ——
  //
  //  綠界開在新分頁之後，這一頁就與付款流程脫鉤了：
  //  付款結果是綠界 server-to-server 送到後端的，瀏覽器收不到任何通知，
  //  而導回 /purchase-result 的是「那一個分頁」，不是這一頁。
  //  所以這一頁只能自己問後端：這張單付了沒。
  //
  //  用輪詢而不是等分頁回報（postMessage / 監聽關閉）：
  //  使用者可能直接把綠界那頁關掉、可能在手機上切走再也沒切回來，
  //  那些情況下回報永遠不會來，但訂單其實已經付掉了。
  //  問後端是唯一不依賴使用者行為的做法。
  const watchOrder = useCallback(
    (orderID) => {
      if (!orderID) return;
      clearInterval(pollRef.current);

      const started = Date.now();
      pollRef.current = setInterval(() => {
        //超過上限就停。不設上限的話，使用者放著不管就是一個
        //  每三秒打一次、永遠不會停的請求迴圈。
        if (Date.now() - started > PAY_POLL_LIMIT_MS) {
          clearInterval(pollRef.current);
          return;
        }

        PaymentService.orderStatus(orderID)
          .then(({ data }) => {
            const status = data?.data?.status;
            if (process.env.NODE_ENV === "development") {
              console.log(`[ECPay] 查單 ${orderID} → ${status || "查無"}`);
            }
            if (status !== "paid") return;
            clearInterval(pollRef.current);
            //付掉了就把這一頁也帶到結果頁 —— 綠界那個分頁已經在那裡了，
            //  兩邊一致，使用者關掉哪一個都不會看到一個停在半路的畫面。
            navigate(`/purchase-result?orderID=${orderID}`, { replace: true });
          })
          //查單失敗不中斷輪詢：可能只是網路斷一下，下一輪就回來了
          .catch(() => {});
      }, PAY_POLL_MS);
    },
    [navigate],
  );

  //離開這一頁就停掉輪詢
  useEffect(() => () => clearInterval(pollRef.current), []);

  //發票填完：建單並跳轉綠界
  const handleCheckout = useCallback(
    (invoice) => {
      if (!pendingPlan || submitting) return;
      setSubmitting(true);
      setPayError("");

      //分頁要在這裡開 —— 這一行還在使用者按下按鈕的那個事件裡。
      //  等建單的 API 回來才開會被瀏覽器當成彈窗擋掉（理由見 ecpay-redirect）。
      const win = openEcpayWindow();

      //—— 動線打點：送出結帳 ——
      //  打在真的要建單之前。最後一段（付款成功）不打點 ——
      //  綠界導回時使用者可能已經關掉分頁，那個數字要從訂單來
      //  （見 config/funnel.js 的說明）。
      track("checkout_submit", "ziwei-jingyue");
      PaymentService.checkoutGuest(pendingPlan.id, invoice, intake)
        .then(({ data }) => {
          if (!data?.status) {
            setPayError(data?.message || "建立訂單失敗");
            win?.close();
            setSubmitting(false);
            return;
          }

          //—— 測試用：把這一單的編號印出來 ——
          //  綠界的付款結果是 server-to-server 送到後端的，瀏覽器永遠看不到，
          //  所以這裡只能給編號，讓人拿它去對後端 log 或查訂單狀態。
          //  正式環境不印：orderID 出現在 console 等於多一個外洩管道。
          if (process.env.NODE_ENV === "development") {
            const id = data.data.params?.MerchantTradeNo;
            console.log(
              `[ECPay] 建單成功 orderID=${id}\n` +
                `  綠界通知會打到後端，看 server 的 [ECPay] log\n` +
                `  或查：curl -s http://localhost:8080/api/payment/order-status/${id}`,
              data.data.params,
            );
          }

          submitToEcpay(data.data.action, data.data.params, win);
          //開在新分頁的話這一頁還在，submitting 要解除 ——
          //  不解除的話使用者在綠界那邊取消、切回來會發現按鈕永遠是暗的。
          if (win) {
            setSubmitting(false);
            watchOrder(data.data.params?.MerchantTradeNo);
          }
        })
        .catch((e) => {
          win?.close();
          setPayError(e?.response?.data?.message || "建立訂單失敗，請稍後再試");
          setSubmitting(false);
        });
    },
    [pendingPlan, submitting, intake, watchOrder],
  );
  //沒帶 state 時：開發中或網址帶 ?preview 就用範例資料頂著，
  //  其餘情況維持原本行為 —— 沒有問診資料就導回劇情頁。

  //—— 四柱 ——
  //  排盤在後端（見 general-route 的 G-3）：四柱牽涉立春換年、節氣換月、
  //  子時跨日與真太陽時校正，而且畫面上這四柱必須跟之後報告裡的完全一致，
  //  所以跟報告共用同一支 getPillars，前端只負責顯示。
  //
  //  失敗就讓框空著，不擋流程 —— 這一格是氣氛，不是必要資訊，
  //  真正會用到四柱的是購買後那份報告，那時後端會自己重算一次。
  const [pillars, setPillars] = useState("");
  //命盤表的資料（十神／天干／地支／藏干）。跟四柱同一支 API 回來的。
  const [chart, setChart] = useState(null);

  useEffect(() => {
    if (!intake) return undefined;

    //元件在請求回來前就被卸載（使用者按了返回）時，不要再 setState
    let alive = true;

    generalService
      .ziweiPillars({
        birthDate: intake.birthDate,
        birthTime: intake.birthTime,
        birthPlace: intake.birthPlace,
        gender: intake.gender,
      })
      .then(({ data }) => {
        if (!alive || !data?.status) return;
        //一柱一行，靠 .jingyue-story__bubble-text 的 pre 斷行
        setPillars((data.data?.pillars || []).join("\n"));
        setChart(data.data || null);
      })
      .catch((e) => console.warn("[鏡月] 四柱取得失敗", e?.message));

    return () => {
      alive = false;
    };
  }, [intake]);

  //—— 四塊命書面板的內文（AI 生的）——
  //
  //  請求不是在這裡發的，是劇情頁在問診倒數第二題就發了（見 jingyue-teaser-cache）。
  //  這裡只是去把那個還在路上的 Promise 接回來 —— 從發出到使用者滑到
  //  第一塊面板，中間隔著兩支影片與整頁 15% 的捲動，約三十秒。
  //
  //  null 有兩種意思：還沒回來、或生成失敗。兩種的畫面是一樣的
  //  （那四塊整個不掛，見 renderBlock 的 panel 分支）——
  //  因為使用者分不出差別，而且這是付費前的鉤子不是商品，
  //  少掉四塊不該擋住整條動線。
  //開場那兩支影片播完了沒。沒播完就不讓底下的長捲頁出現 ——
  //  影片是蓋在上面的（fixed），但底下的頁面還是會吃捲動，
  //  不擋的話使用者可以一邊看影片一邊把故事滑掉。
  const [introDone, setIntroDone] = useState(false);

  //—— 四塊命書面板的內文（AI 生的）——
  //
  //  —— 為什麼是「按下開始解命」才發，不是一掛載就發 ——
  //  這支生成是付費前的成本：每個走到這裡的人都要燒一次，買不買都一樣。
  //  掛載就發的話，在第一支影片（9.5 秒）中途關掉頁面的人也會被算一次，
  //  那是純浪費。「按下開始解命」是一個明確的互動，意願訊號強得多。
  //
  //  時間還夠不夠：按下去之後還有 jingyue-5（她開口 10 秒，不能跳過），
  //  加上要滑到整頁的 15% 才看得到第一塊面板（10-20 秒）——
  //  掩護 18 到 28 秒，而生成實測 6 秒。綽綽有餘。
  //
  //  startTeaser 是單發的：同一份問診資料只會真的打一次
  //  （React 嚴格模式會讓事件處理器以外的東西跑兩遍，見 jingyue-teaser-cache）。
  //
  //  null 有兩種意思：還沒回來、或生成失敗。兩種的畫面一樣
  //  （那四塊整個不掛，見 renderBlock 的 panel 分支）——
  //  使用者分不出差別，而且這是付費前的鉤子不是商品，
  //  少掉四塊不該擋住整條動線。
  const [panels, setPanels] = useState(null);

  //開場那顆「開始解命」按下去時由開場元件回呼。
  //  aliveRef 而不是 effect 的區域變數：這不是 effect，是事件處理器 ——
  //  使用者可能在生成回來之前就按返回離開，那時 setPanels 會對著
  //  已卸載的元件呼叫。
  //
  //  ⚠️ 進場時一定要把它設回 true，不能只靠 useRef(true) 的初始值：
  //  嚴格模式下 effect 會跑「掛載 → cleanup → 再掛載」，
  //  cleanup 把它翻成 false，而第二次掛載不會重新執行 useRef 的初始值 ——
  //  結果就是從頭到尾都是 false，setPanels 永遠不會被呼叫，
  //  四塊面板明明生成成功卻一塊都不出現。
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const beginTeaser = useCallback(() => {
    if (!intake) return;
    startTeaser(intake).then((data) => {
      if (aliveRef.current) setPanels(data);
    });
  }, [intake]);

  //—— 底部按鈕的出現時機 ——
  //  觸發之後就 disconnect：按鈕只出現一次，往回捲不會又消失。
  //
  //  ⚠️ 一定要等開場播完才掛這個 observer。兩個理由，第二個是會出錯的那個：
  //
  //  1. 開場期間整頁被影片蓋著也不能捲，這時候讓按鈕冒出來沒有意義。
  //
  //  2. 頁面剛掛載時那十九張圖都還沒有高度，整頁只有幾百 px ——
  //     觸發點最終在 2653px，但那一瞬間它就在視窗裡。observer 會立刻
  //     判定「滑到了」然後 disconnect，按鈕就從此固定在畫面上。
  //     這個 bug 只有在圖沒進快取時才會出現（冷啟動、第一次進站的真實使用者），
  //     開發時重整幾次之後反而看不到 —— 所以它很容易被漏掉。
  //
  //     等開場那十七秒過去，圖早就載完、版面也定了，量到的位置才是真的。
  useEffect(() => {
    if (!intake || !introDone) return undefined;

    const target = document.getElementById(CTA_TRIGGER_ID);
    const root = scrollRef.current;
    if (!target || !root) return undefined;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setShowCta(true);
        io.disconnect();
      },
      { root },
    );
    io.observe(target);

    return () => io.disconnect();
  }, [intake, introDone]);

  //沒有問診資料就不該停在這一頁（直接貼網址、或重新整理）。
  //  replace 而不是 push：這一頁不該留在返回堆疊裡，
  //  不然使用者回上一頁又會被彈回來一次。
  useEffect(() => {
    if (!intake) navigate(BACK_TO, { replace: true });
  }, [intake, navigate]);

  //返回：有上一頁就回上一頁，沒有就回劇情頁。
  //  直接貼網址進來時 history 沒有上一頁，navigate(-1) 會把人送出本站
  //  （退路判斷同 sub-header 與劇情頁）。
  const handleBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate(BACK_TO, { replace: true });
  };

  //導頁是在 effect 裡做的（render 當下還不能導），所以這一幀先不畫東西 ——
  //  畫了會閃一下沒有前提的空頁面。
  if (!intake) return null;

  return (
    <div
      className={`jingyue-story${introDone ? "" : " is-intro"}`}
      ref={scrollRef}
    >
      {/* 開場：他看完竹冊、然後批命。這兩幕原本在劇情頁，
          搬過來是為了把生成那四塊命書的時間藏在劇情底下（見上方 panels 那段）。
          播完就卸載 —— 留著的話那兩支影片會一直佔著記憶體與解碼器，
          iOS 上同時掛太多 <video> 會讓後面的影片播不出來。 */}
      {!introDone && (
        <JingyueStoryIntro
          onReading={beginTeaser}
          onDone={() => setIntroDone(true)}
        />
      )}
      {/* 返回鍵浮在內容之上。不用站內的 SubHeader ——
          那是一條有底色的頂部列，會把這一頁從「戲的延續」拉回「網站的次層頁」。 */}
      <button
        type="button"
        className="jingyue-story__back"
        onClick={handleBack}
        aria-label="返回"
      >
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
          <path
            d="M15 5l-7 7 7 7"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* 一個個 block 串下去。中間沒有間隙 ——
          圖和圖、圖和文之間要接得像同一張長圖，不是一段段的卡片。
          結尾的行動按鈕等內容定案再加，現在整頁就是故事本身。 */}
      <main className="jingyue-story__blocks">
        {BLOCKS.map((block, i) =>
          renderBlock(block, i, {
            pillars,
            chart,
            name: intake.name,
            birthDate: intake.birthDate,
            //四塊命書的內文。panels 還沒回來（或失敗）時是 undefined，
            //  那四塊就不會掛上來 —— 見上方 panels 那段說明。
            //命盤四乘四（block 13）。跟四柱同一支 API 回來的。
            board: chart?.命盤格,
            situation: panels?.situation,
            daXian: panels?.daXian,
            caiYun: panels?.caiYun,
            aiQing: panels?.aiQing,
          }),
        )}
      </main>

      {/* 固定在底部的行動按鈕。外層是全站共用的 .app-fixed ——
          它貼著欄位而不是視窗邊緣，桌機上才不會跑到畫面最旁邊。 */}
      {showCta && (
        <div className="app-fixed jingyue-story__cta-layer">
          {/* 按鈕上的字已經畫在圖裡，所以不放文字節點。
              aria-label 仍然要給 —— 讀屏看不到圖上的字，
              沒有它這顆按鈕會被唸成「按鈕」而已。 */}
          <button
            type="button"
            className="jingyue-story__cta"
            aria-label="請鏡月照見命盤"
            style={{ "--cta-bg": `url(${IMG}jingyue-story-btn.png)` }}
            onClick={() => {
              //—— 動線打點：開啟購買視窗 ——
              track("checkout_open", "ziwei-jingyue");
              setBuying(true);
            }}
          />
        </div>
      )}

      <PurchaseSheet
        open={buying}
        plans={plans}
        //  鏡月的重點色。不給的話會用白夜那組薄荷綠 ——
        //  故事頁一路紫到底，結帳卻跳出一張綠的單子。
        theme="violet"
        onClose={() => setBuying(false)}
        onSubmit={handlePickPlan}
      />

      {/* 發票資訊。在跳轉綠界之前問 ——
          電子發票是法規要求，而綠界付完款不會再問一次。
          預設信箱帶問診填的那個：這個流程沒有登入，抓不到 Google 信箱。 */}
      {pendingPlan && (
        <InvoiceSheet
          open
          amount={pendingPlan.price}
          defaultEmail={intake.email}
          defaultName={intake.name}
          submitting={submitting}
          onClose={() => {
            setPendingPlan(null);
            setPayError("");
          }}
          onConfirm={handleCheckout}
        />
      )}

      {/* 建單失敗。用浮動提示而不是換頁 ——
          使用者還在故事頁上，把他導走等於要他從頭再來一次。 */}
      {payError && (
        <div className="jingyue-story__pay-error" role="alert">
          {payError}
        </div>
      )}
    </div>
  );
};

export default JingyueStoryPage;
